//may11 FormLayoutContext.js without embedd iframe fixes:

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { getLayoutConfig, serializeLayoutConfig, getDefaultLayout, deleteFieldFromForm, saveLayoutConfig, getDefaultFieldMetadata, getLayoutMeta } from '../utils/firestore'; // Add saveLayoutConfig import
import { useFormContext } from './FormContext'; // Use formId from FormContext
import { useFieldMetadata } from './FieldMetadataContext'; // Import useFieldMetadata to manage field metadata
import { useAuth } from './AuthContext';

import { getManagedFieldById } from '../utils/managedFields';
import { buildManagedFieldInstance } from '../utils/managedFieldInstances';
import { resolveManagedDropdownInstanceOptions } from '../utils/managedFieldRuntime';

import { v4 as uuidv4 } from 'uuid';
import { decompressData } from '../utils/decompressionUtils'; // Import decompression utility
import { safeSetItem, safeGetItem, safeRemoveItem, safeRemoveFieldData } from '../utils/storageUtils'; // Import safeRemoveItem

// AUF restore: mark restored entries on the form doc
import { db } from '../firebase';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';

/**
 * Ensure `submitRow` exists, contains only the submit button, and is placed last.
 */
const adjustSubmitRow = (layout) => {
//    console.log("[adjustSubmitRow] Adjusting submitRow structure...");

    if (!layout || typeof layout !== 'object') {
        console.error("[adjustSubmitRow] Invalid layout provided.");
        return layout;
    }

    let submitRow = layout['submitRow'] || []; // Get the existing submitRow or create an empty one
    let newLayout = { ...layout };

    // Collect misplaced submit buttons
    let submitFields = [];
    Object.keys(newLayout).forEach(rowKey => {
        if (rowKey !== 'submitRow') {
            newLayout[rowKey] = newLayout[rowKey].filter(field => {
                if (field.type === 'submit') {
                    submitFields.push(field); // Move submit button(s) to submitRow
                    return false; // Remove from its current row
                }
                return true;
            });
        }
    });

    // Ensure submitRow contains only the submit button
    submitRow = submitFields.length > 0 ? submitFields : submitRow;
    newLayout['submitRow'] = submitRow;

    // Reorder rows to ensure submitRow is last
    const reorderedLayout = Object.keys(newLayout)
        .filter(key => key !== 'submitRow')
        .sort((a, b) => parseInt(a.replace('row', ''), 10) - parseInt(b.replace('row', ''), 10))
        .reduce((obj, key) => {
            obj[key] = newLayout[key];
            return obj;
        }, {});

    reorderedLayout['submitRow'] = submitRow; // Always add submitRow last

//    console.log("[adjustSubmitRow] Adjusted layout:", reorderedLayout);
    return reorderedLayout;
};

const FormLayoutContext = createContext();

export const useFormLayout = () => useContext(FormLayoutContext);

export const FormLayoutProvider = ({ children }) => {
const { formId, formIdReady, formDetails } = useFormContext();
const { organizationId, currentUser } = useAuth() || {};

const { fieldsMetadata, updateFieldMetadata, addFieldMetadata, deleteFieldMetadata, isLoading: fieldsLoading } = useFieldMetadata();

// ────────────────────────────────────────────────────────────────
// Layout version mode (Published vs Draft Overlay vs Auto)
// - 'auto'       : prefer draftOverlay when present, else published/root
// - 'published'  : force root/published layoutConfig
// - 'draftOverlay': force draftOverlay layoutConfig (when present)
// Stored in sessionStorage so refresh keeps the same view.
// ────────────────────────────────────────────────────────────────
const [layoutVersionMode, setLayoutVersionMode] = useState(() => {
  try {
    const raw = sessionStorage.getItem(`form_${formId}_layoutVersionMode`);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed?.value || 'auto';
  } catch (e) {
    return 'auto';
  }
});

    const [layoutConfig, setLayoutConfig] = useState({});

    const [showDropZones, setShowDropZones] = useState(false);

/**
 * Tracks which field is currently being dragged (if any).
 * Used for debugging + to prevent non-drag code from fighting drag UI state.
 */
const [activeDragId, setActiveDragId] = useState(null);

    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const [selectedField, setSelectedField] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [layoutModified, setLayoutModified] = useState(false); // State to track modifications

    // ────────────────────────────────────────────────────────────────
    // Route-aware flags (builder vs viewer) – mirrors FormThemeContext
    // ────────────────────────────────────────────────────────────────
    const isPublicPreviewMode = useMemo(() => {
      try {
        const raw = sessionStorage.getItem('isPublicPreview');
        const parsed = raw ? JSON.parse(raw) : null;
        return parsed?.value === true;
      } catch (e) {
        return false;
      }
    }, []);

    const isBuilderRoute = useMemo(() => {
      if (typeof window === 'undefined') return false;
      return window.location.pathname.startsWith('/form-builder');
    }, []);

    // ────────────────────────────────────────────────────────────────
    // Route classification
    //
    // Builder route:
    //  • authoring surface
    //  • may use storage as first-paint state
    //  • may repair/ensure metadata
    //  • may persist layout edits locally before save/publish
    //
    // Public preview mode:
    //  • screenshot/headless infrastructure
    //  • keeps existing preview behavior
    //
    // Viewer route:
    //  • /forms/:formId
    //  • /embed/forms/:formId
    //  • must render published snapshots
    //  • must not repair/mutate metadata at runtime
    // ────────────────────────────────────────────────────────────────
    const isViewerRoute = useMemo(() => {
      return !isBuilderRoute && !isPublicPreviewMode;
    }, [isBuilderRoute, isPublicPreviewMode]);

    // ────────────────────────────────────────────────────────────────
    // IMPORTANT: Viewer route must never show draft overlays
    // - Builder route: allow auto/published/draftOverlay selection
    // - Viewer route (/forms/:id): force published blobs
    // ────────────────────────────────────────────────────────────────
    const effectiveViewMode = useMemo(() => {
      // Only the builder should ever honor layoutVersionMode.
      // Anything else must show the published version.
      if (isBuilderRoute) return layoutVersionMode;
      return 'published';
    }, [isBuilderRoute, layoutVersionMode]);

    // Persist mode so refresh keeps the same view choice
    useEffect(() => {
      if (!formId) return;

      // Only builder should persist a builder-only preference
      if (!isBuilderRoute) return;

      try {
        sessionStorage.setItem(
          `form_${formId}_layoutVersionMode`,
          JSON.stringify({ value: layoutVersionMode, timestamp: Date.now() })
        );
      } catch (e) {
        // non-fatal
      }
    }, [formId, layoutVersionMode, isBuilderRoute]);


//  Mode-aware layout cache key (prevents “published” cache masking “draft overlay” and vice versa)
const localLayoutKey = useMemo(() => {
  return `form_${formId}_layoutConfig__${effectiveViewMode}`;
}, [formId, effectiveViewMode]);

//  NEW: Mode-aware metadata cache key (aligns with FieldMetadataContext)
const localFieldsMetadataKey = useMemo(() => {
  return `form_${formId}_fieldsMetadata__${effectiveViewMode}`;
}, [formId, effectiveViewMode]);

// Legacy (pre-mode-aware) metadata key (kept for migration / fallback)
const legacyFieldsMetadataKey = useMemo(() => {
  return `form_${formId}_fieldsMetadata`;
}, [formId]);

// Mode-aware dropdownOptions cache key (aligned with MultiAnswerFieldContext)
const localDropdownOptionsKey = useMemo(() => {
  return `form_${formId}_dropdownOptions__${effectiveViewMode}`;
}, [formId, effectiveViewMode]);

// Legacy dropdownOptions key (kept for migration / fallback)
const legacyDropdownOptionsKey = useMemo(() => {
  return `form_${formId}_dropdownOptions`;
}, [formId]);

// Mode-aware dropdownOptions lastUpdated key
const localDropdownLastUpdatedKey = useMemo(() => {
  return `form_${formId}_dropdownLastUpdated__${effectiveViewMode}`;
}, [formId, effectiveViewMode]);

// Legacy dropdownOptions lastUpdated key
const legacyDropdownLastUpdatedKey = useMemo(() => {
  return `form_${formId}_dropdownLastUpdated`;
}, [formId]);


    // ────────────────────────────────────────────────────────────────
    // AUF Restore (pending) — org-scoped so it cannot bleed across orgs.
    // The Settings page writes this, and the builder consumes it once.
    // ────────────────────────────────────────────────────────────────
    const AUF_RESTORE_KEY = useMemo(() => {
      return organizationId ? `org_${organizationId}_aufRestorePending` : null;
    }, [organizationId]);


// Track active metadata creation states to prevent cleanup collisions
// Split into two maps so we never mix Promise vs timeout handles.
const activeMetadataPromises = useRef(new Map());   // Tracks fieldId -> Promise
const activeMetadataTimeouts = useRef(new Map());   // Tracks fieldId -> timeout reference

const processedFields = useRef(new Set());          // Tracks processed fields to prevent redundant updates
const metadataEnsured = useRef(false);              // Prevents redundant full-layout metadata enforcement
const activeFetchRequest = useRef(null);            // Tracks active fetch requests to prevent race conditions

// ────────────────────────────────────────────────────────────────
// Enterprise-grade: cancel any in-flight Firestore hydrate
// The moment we accept a local/builder mutation, we abort the active
// fetch so its eventual resolution can never overwrite the user's edit.
// This is NOT a bandaid — it's the standard "cancel stale work" pattern.
// ────────────────────────────────────────────────────────────────
const abortActiveFetch = useCallback((reason = 'unknown') => {

    try {
        const ctrl = activeFetchRequest.current;
        if (ctrl && typeof ctrl.abort === 'function') {
            console.warn("[FormLayoutContext] Aborting active fetchLatestLayout due to newer local change:", {
                reason,
                formId,
                localLayoutKey,
                effectiveViewMode,
            });
            ctrl.abort();
        }
    } catch (e) {
        console.warn("[FormLayoutContext] Failed to abort active fetchLatestLayout (non-fatal):", e?.message || e);
    } finally {
        // Always clear so future fetches can run
        activeFetchRequest.current = null;
    }
}, [formId, localLayoutKey, effectiveViewMode]);

// ────────────────────────────────────────────────────────────────
// Reset per-form/per-mode guards so processedFields does not
// permanently block metadata updates across formId/mode changes.
// This is a scoped cache, NOT a lifetime cache.
// ────────────────────────────────────────────────────────────────
useEffect(() => {
    processedFields.current = new Set();
    metadataEnsured.current = false;

    // Also clear any active metadata creation tracking so we don't leak
    // across form switches / mode switches.
    activeMetadataPromises.current = new Map();
    activeMetadataTimeouts.current = new Map();
}, [formId, effectiveViewMode]);

// ────────────────────────────────────────────────────────────────
// Enterprise-grade hydration safety:
// These refs guarantee that an async Firestore hydrate can NEVER
// overwrite a newer local user edit (drop/move/delete).
// - layoutEditEpoch bumps on ANY user layout mutation.
// - fetchSeq bumps on each fetchLatestLayout start.
// ────────────────────────────────────────────────────────────────
const layoutEditEpoch = useRef(0);
const fetchSeq = useRef(0);

// ────────────────────────────────────────────────────────────────
// Managed field preview detection
// - A managed field preview is a side-panel drag item that represents a
//   centrally governed org-managed field definition
// - These items must NOT use the generic local field creation path
// - Instead, they must be materialized through buildManagedFieldInstance(...)
//   so the form gets:
//      • a new local fieldId
//      • a new local ufi
//      • stable managed-field references
// ────────────────────────────────────────────────────────────────
const isManagedFieldPreviewItem = useCallback((item) => {
    return (
        !!item &&
        typeof item === 'object' &&
        item.isManagedFieldPreview === true &&
        typeof item.managedFieldId === 'string' &&
        item.managedFieldId.trim().length > 0
    );
}, []);

// ────────────────────────────────────────────────────────────────
// AUF restore guard: prevents ensureMetadataForLayout() from racing
// and calling addFieldMetadata() defaults for a just-restored field.
// ────────────────────────────────────────────────────────────────
const restoredFieldIds = useRef(new Set());


const ensureMetadataForLayout = useCallback((layout) => {
//    console.log("[FormLayoutContext: ensureMetadataForLayout] Starting metadata recovery for layout.");

    // ENTERPRISE:
    // Metadata recovery/repair is an authoring concern, not a public-runtime concern.
    //
    // Builder route:
    //  • may repair missing metadata
    //  • may add default metadata for newly dropped fields
    //  • may normalize header/image metadata before save/publish
    //
    // Public preview mode:
    //  • keeps existing screenshot/headless rendering behavior.
    //
    // Viewer routes (/forms/:formId and /embed/forms/:formId):
    //  • must render the published metadata snapshot as-is
    //  • must not call updateFieldMetadata(...)
    //  • must not create missing metadata
    //  • must not mutate image/header metadata at runtime
    if (isViewerRoute) {
        return;
    }

    // SCENARIO CHECK: 1) If FieldMetadataContext is still loading
    // Exit early to avoid overwriting user alignment with "center"
    // ─────────────────────────────────────────────
    if (fieldsLoading) {
    //        console.log("[FormLayoutContext: ensureMetadataForLayout] FieldMetadataContext is still loading; skipping ensureMetadataForLayout this round.");
        return;
    }


    // ─────────────────────────────────────────────
    // SCENARIO CHECK: 2) If a new form is being created
    // (This is conceptual; if you have a real boolean or condition, place it here)
    // ─────────────────────────────────────────────
    if (/* e.g. isNewFormScenario() */ false) {
//        console.log("[FormLayoutContext: ensureMetadataForLayout] New form creation scenario detected; continuing as normal...");
        // Not returning, but you might have logic to handle brand-new forms
    }

    // ─────────────────────────────────────────────
    // SCENARIO CHECK: 3) If form is saved/published but user has made updates
    // (Again, adapt if you have a real condition e.g. formPublishStatus === 'published')
    // ─────────────────────────────────────────────
    if (/* formPublishStatus === 'published' */ false) {
//        console.log("[FormLayoutContext: ensureMetadataForLayout] Form is published but user is updating. We'll ensure metadata carefully...");
        // You can add special logs or code as needed, but not returning
    }

    if (!formId) {
        console.error("[FormLayoutContext: ensureMetadataForLayout] formId is not available, skipping.");
        return;
    }

    if (metadataEnsured.current) {
//        console.log("[FormLayoutContext: ensureMetadataForLayout] Metadata already ensured, skipping redundant execution.");
        return;
    }

//    console.log("[FormLayoutContext: ensureMetadataForLayout] Layout passed to function:", JSON.stringify(layout, null, 2));

    // **ADDED**: Log what’s in your in-memory fieldsMetadata right now
//    console.log("[FormLayoutContext: ensureMetadataForLayout] Current in-memory fieldsMetadata:", fieldsMetadata);

    // Step 1: Collect fields that require metadata recovery
    const fieldsNeedingMetadata = [];

Object.values(layout).flat().forEach(field => {
//        console.log("[FormLayoutContext: ensureMetadataForLayout] Checking field with ID:", field.id, "and UFI:", field.ufi);

        // ─────────────────────────────────────────────────────────────
        // AUF restore guard: if this field was just restored, do NOT let
        // ensureMetadataForLayout treat it as "missing" and trigger
        // addFieldMetadata() defaults that overwrite restored settings.
        // ─────────────────────────────────────────────────────────────
        if (field?.id && restoredFieldIds.current.has(field.id)) {
            console.log(
              "[FormLayoutContext: ensureMetadataForLayout] Skipping newly restored field during ensure pass:",
              { fieldId: field.id, ufi: field.ufi, type: field.type }
            );
            return;
        }

        // Skip if field has been processed already
        if (!field.id || processedFields.current.has(field.id)) {
//            console.log(`[FormLayoutContext: ensureMetadataForLayout] Skipping already processed field ID: ${field.id}`);
            return;
        }

        // Validate essential properties
        if (!field.type) {
            console.error(`[FormLayoutContext: ensureMetadataForLayout] Field type is missing for field ID: ${field.id}`);
            return;
        }

        const existingMeta = fieldsMetadata[field.id];
//        console.log(
//            `[FormLayoutContext: ensureMetadataForLayout] existingMeta for field ID: ${field.id} =>`,
//            existingMeta
//        );

        // Handle submit button logic (handled exclusively in layout)
        if (field.type === 'submit') {
            processedFields.current.add(field.id);
            return;
        }

        // Step 2: Identify missing or incomplete metadata
        if (!existingMeta) {
//            console.log(
//                `[FormLayoutContext: ensureMetadataForLayout] **NO in-memory metadata** for field ID ${field.id} - adding to recovery list.`
//            );
            fieldsNeedingMetadata.push({ ...field });
            return;
        }


        // Step 3: Validate and correct metadata inconsistencies
        const updatedMetadata = { ...existingMeta };

//        console.log(`[FormLayoutContext: ensureMetadataForLayout] 🔄 Updating metadata for field ID: ${field.id}`, updatedMetadata);

        // 🔹 Image Field Metadata Correction
        if (field.type === 'image') {
            updatedMetadata.assetReference = existingMeta.assetReference || null;
            updatedMetadata.allowedImageTypes = existingMeta.allowedImageTypes || ['banner', 'icon'];
            updatedMetadata.style = {
                ...existingMeta.style,
                width: field.style?.width || '100%',
                height: field.style?.height || 'auto',
            };
        }

        // 🔹 Header Field Metadata Correction
        // 🔹 Header Field Metadata Correction (Updated for Schema Alignment)
        if (field.type === 'header') {
            updatedMetadata.header = existingMeta.header || "New Header Text";

            //  Ensure `enableSubheader` properly retains `false` values
            updatedMetadata.enableSubheader = 
                existingMeta.enableSubheader !== undefined
                    ? existingMeta.enableSubheader
                    : false;

            updatedMetadata.subheader = existingMeta.subheader || "";

            //  Updated to ensure alignment with new schema for `headerStyle` and `subheaderStyle`
            updatedMetadata.headerStyle = {
                ...existingMeta.headerStyle,
                fontSize: existingMeta.headerStyle?.fontSize || "24px",
                headerColor: existingMeta.headerStyle?.headerColor || "#000000",
                textAlign: existingMeta.headerStyle?.textAlign || "center",
                fontWeight: existingMeta.headerStyle?.fontWeight || "normal",
                margin: existingMeta.headerStyle?.margin || "0px 0",
                padding: existingMeta.headerStyle?.padding || "0px 0",
            };

            updatedMetadata.subheaderStyle = {
                ...existingMeta.subheaderStyle,
                subFontSize: existingMeta.subheaderStyle?.subFontSize || "16px",
                subheaderColor: existingMeta.subheaderStyle?.subheaderColor || "#666666",
                subheaderFontWeight: existingMeta.subheaderStyle?.subheaderFontWeight || "normal",
                textAlign: existingMeta.subheaderStyle?.textAlign || "center",
                margin: existingMeta.subheaderStyle?.margin || "0px 0",
                padding: existingMeta.subheaderStyle?.padding || "0px 0",
            };

            // 🚨 Remove outdated `.style` property to ensure alignment with new schema
            if (updatedMetadata.style) {
                console.warn(`[FormLayoutContext: ensureMetadataForLayout] Removing outdated 'style' property for header field ID: ${field.id}`);
                delete updatedMetadata.style;
            }
        }
        updateFieldMetadata(field.id, updatedMetadata);
        processedFields.current.add(field.id);
    });
    
    // Step 4: Create missing metadata asynchronously using `addFieldMetadata`
    if (fieldsNeedingMetadata.length > 0) {
//        console.log("[FormLayoutContext: ensureMetadataForLayout] Creating missing metadata for fields:", fieldsNeedingMetadata);

        const creationPromises = fieldsNeedingMetadata.map(async (field) => {
//        console.log(`[FormLayoutContext: ensureMetadataForLayout] For field ${field.id}, current isLoading: ${isLoading}`);

            //  Prevent duplicate metadata creation using activeMetadataPromises/Timeouts
            if (activeMetadataPromises.current.has(field.id) || activeMetadataTimeouts.current.has(field.id)) {
//                console.log(`[FormLayoutContext: ensureMetadataForLayout] Skipping duplicate metadata creation for field ID: ${field.id}`);
                return; // Skip duplicate requests
            }

            const metadataPromise = Promise.race([
                addFieldMetadata(field.id, field.type, field.ufi || null, field.optionsSetUFI || null),
                new Promise((resolve) => setTimeout(resolve, 3000))  // Buffer delay for slow network
            ]);

            activeMetadataPromises.current.set(field.id, metadataPromise);

            try {
                await metadataPromise;

                //  Clean up temporary metadata if present
                updateFieldMetadata(field.id, { isLoading: false });

//                console.log(`[FormLayoutContext: ensureMetadataForLayout]  Metadata creation completed successfully for field ID: ${field.id}`);
            } catch (error) {
                console.error(`[ensureMetadataForLayout] ❗ Error adding metadata for field ID: ${field.id}`, error);

                //  Cleanup on failure
                deleteFieldMetadata(field.id);
            } finally {
                activeMetadataPromises.current.delete(field.id); //  Clean up active creation tracking
                activeMetadataTimeouts.current.delete(field.id); //  Clean up timeout tracking (if any)
            }

        });

        // Step 5: Await all creation processes to avoid race conditions
        Promise.all(creationPromises).then(() => {
//            console.log("[FormLayoutContext: ensureMetadataForLayout] Successfully ensured metadata for missing fields.");
        });
    }

//    console.log(`[FormLayoutContext: ensureMetadataForLayout]  Metadata creation finished for all fields. Triggering UI render...`);

    metadataEnsured.current = true; // Mark metadata as ensured
//    console.log("[FormLayoutContext: ensureMetadataForLayout] Finished ensuring metadata for layout.");

    // **ADDED**: Finally, see the entire fieldsMetadata after we do the above
//    console.log("[FormLayoutContext: ensureMetadataForLayout] Final metadata state snapshot:", fieldsMetadata);

}, [
  fieldsMetadata,
  fieldsLoading,
  addFieldMetadata,
  updateFieldMetadata,
  deleteFieldMetadata,
  formId,
  isViewerRoute,
]);



    /**
     * Generate a basic hash for layout comparison.
     */
const stableStringify = (value) => {
  const seen = new WeakSet();

  const sorter = (k, v) => {
    if (v && typeof v === 'object') {
      if (seen.has(v)) return; // avoid circular refs
      seen.add(v);

      // Sort plain objects by keys (arrays preserve order)
      if (!Array.isArray(v)) {
        const out = {};
        Object.keys(v).sort().forEach((key) => {
          out[key] = v[key];
        });
        return out;
      }
    }
    return v;
  };

  return JSON.stringify(value, sorter);
};

// UTF-8 safe base64 (prevents btoa() crashes on Unicode labels)
const toBase64Utf8 = (str) => {
  const bytes = new TextEncoder().encode(str);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
};

const generateHash = (layout) => {
  // Ensure object properties are ordered consistently for hashing (deep-stable)
  const orderedLayout = stableStringify(layout);
  return toBase64Utf8(orderedLayout);
};

 
/**
 * Fetch the latest layout for the form, with decompression and sessionStorage logic.
 */
const fetchLatestLayout = useCallback(async () => {

    // Prevent concurrent fetch requests (Race Condition Prevention)
    if (activeFetchRequest.current) {
        console.warn("[FormLayoutContext: fetchLatestLayout] Another fetch request is already active. Skipping this call.");
        return;
    }

    // monotonic hydration guard markers
    // - myFetchId: identifies this fetch call (newer fetches supersede older)
    // - epochAtStart: identifies whether user edits happened during this fetch
    const myFetchId = ++fetchSeq.current;
    const epochAtStart = layoutEditEpoch.current;

    // Track the active fetch request
    const fetchController = new AbortController();
    activeFetchRequest.current = fetchController;


    try {
        if (!formId) {
            console.warn("[FormLayoutContext: fetchLatestLayout] Form ID is not available, skipping layout fetch.");
            return;
        }

//        console.log("[FormLayoutContext: fetchLatestLayout] Fetching latest layout for formId:", formId);

        // Check if layout and its hash are in session/local storage
        const storedLayout = safeGetItem(localLayoutKey);
        const storedLayoutHash = safeGetItem(`${localLayoutKey}_hash`); // Stored hash for the layout
//        console.log("Retrieved stored hash:", storedLayoutHash);

        if (storedLayout) {
//            console.log("[FormLayoutContext: fetchLatestLayout] Using layout from session storage:", JSON.stringify(storedLayout, null, 2));

            const currentLayoutHash = generateHash(storedLayout);
//            console.log("[FormLayoutContext: fetchLatestLayout] Current computed hash:", currentLayoutHash);

            // Ensure metadata is always synced with the stored layout
            if (!metadataEnsured.current) {
                ensureMetadataForLayout(storedLayout); // Always sync metadata, regardless of source
                metadataEnsured.current = true;
            }

            //  Ensure submitRow is structured correctly
            const adjustedLayout = adjustSubmitRow(storedLayout);

            setLayoutConfig(prevLayout => {
                // Only update state if layout is different to avoid unnecessary re-renders
                if (JSON.stringify(prevLayout) !== JSON.stringify(adjustedLayout)) {
//                    console.log("[FormLayoutContext: fetchLatestLayout] Updating layoutConfig from stored session layout.");
                    return adjustedLayout;
                }
//                console.log("[FormLayoutContext: fetchLatestLayout] Stored session layout matches current layoutConfig, skipping state update.");
                return prevLayout;
            });

            setLayoutModified(false);
            return; // Skip further processing if layout is found in session storage
        }

        // Fetch layout from Firestore
//        console.log("[FormLayoutContext: fetchLatestLayout] No layout found in session storage. Fetching from Firestore.");

        const layoutResponse = await getLayoutConfig(formId, {
          signal: fetchController.signal,

          // Viewer route forces published; builder uses the toggle
          viewMode: effectiveViewMode,
        });


//        console.log(`[FormLayoutContext: fetchLatestLayout] Layout response received from Firestore for formId: ${formId}`, layoutResponse);

        if (layoutResponse.status === 'success' && layoutResponse.layout) {
            let layoutToSet = layoutResponse.layout;

            // Only decompress if the layout is a Base64-encoded string
            if (typeof layoutToSet === 'string') {
//                console.log("[FormLayoutContext: fetchLatestLayout] Detected compressed layout, decompressing...");
                try {
                    layoutToSet = await decompressData(layoutToSet); // Decompress layout
//                    console.log("[FormLayoutContext: fetchLatestLayout] Layout decompressed successfully:", layoutToSet);
                } catch (decompressionError) {
                    console.error("[FormLayoutContext: fetchLatestLayout] Error decompressing layout:", decompressionError);
                    throw decompressionError; // Rethrow to ensure error handling
                }
            }

            // Calculate hash for comparison with stored hash
            const newLayoutHash = generateHash(layoutToSet);
//            console.log("[FormLayoutContext: fetchLatestLayout] New layout hash:", newLayoutHash);

            // If the stored layout hash matches the newly fetched layout's hash, no need to update state
            if (storedLayoutHash === newLayoutHash) {
//                console.log("[fetchLatestLayout] Fetched layout hash matches stored layout hash, skipping update.");
                return;
            }

            //  Ensure metadata is synced for Firestore fetched layout
            if (!metadataEnsured.current) {
                ensureMetadataForLayout(layoutToSet); // Sync metadata before saving
                metadataEnsured.current = true;
            }

            //  Ensure submitRow is structured correctly
            const adjustedLayout = adjustSubmitRow(layoutToSet);

            // ─────────────────────────────────────────────────────────────
            //  NEW: Enterprise-grade stale-safe hydration guard
            // If the user edited the layout AFTER this fetch started, then
            // applying this Firestore result would clobber the user's edits.
            // We refuse to apply stale results.
            // ─────────────────────────────────────────────────────────────
            if (layoutEditEpoch.current !== epochAtStart) {
                console.warn("[FormLayoutContext: fetchLatestLayout] Ignoring stale Firestore layout (local edits occurred during fetch).", {
                    myFetchId,
                    epochAtStart,
                    epochNow: layoutEditEpoch.current,
                    formId,
                    localLayoutKey,
                    effectiveViewMode,
                });
                return;
            }

            if (myFetchId !== fetchSeq.current) {
                console.warn("[FormLayoutContext: fetchLatestLayout] Ignoring stale Firestore layout (newer fetch started).", {
                    myFetchId,
                    latestFetchId: fetchSeq.current,
                    formId,
                    localLayoutKey,
                    effectiveViewMode,
                });
                return;
            }

            // Use the decompressed or raw layout
            setLayoutConfig(prevLayout => {
                if (JSON.stringify(prevLayout) !== JSON.stringify(adjustedLayout)) {
//                    console.log("[FormLayoutContext: fetchLatestLayout] Updating layoutConfig with fetched Firestore layout.");
                    return adjustedLayout;
                }
//                console.log("[FormLayoutContext: fetchLatestLayout] Fetched Firestore layout matches current layoutConfig, skipping state update.");
                return prevLayout;
            });

            setLayoutModified(false);
//            console.log("[FormLayoutContext: fetchLatestLayout] Layout successfully set after decompression:", adjustedLayout);

            // Store layout and its hash in session storage
            safeSetItem(localLayoutKey, adjustedLayout);
            safeSetItem(`${localLayoutKey}_hash`, newLayoutHash); // Save the hash for future comparison

        } else if (layoutResponse.status === 'default') {
            // Handle the case when no layout is found in Firestore and default layout is provided
            console.warn(`[FormLayoutContext: fetchLatestLayout] No layout found in Firestore for formId: ${formId}. Using default layout.`);

    const defaultLayout = getDefaultLayout();  //  Directly generate the default layout
//    console.log("[FormLayoutContext: fetchLatestLayout] Default layout generated:", JSON.stringify(defaultLayout, null, 2));

//            const defaultLayout = layoutResponse.layout;

            //  Ensure metadata and `submitRow` are properly handled
            if (!metadataEnsured.current) {
                ensureMetadataForLayout(defaultLayout);
                metadataEnsured.current = true;
            }

            const adjustedLayout = adjustSubmitRow(defaultLayout);
//    console.log("[FormLayoutContext: fetchLatestLayout] Adjusted default layout before setting:", JSON.stringify(adjustedLayout, null, 2));

            // Store default layout in session storage
            setLayoutConfig(prevLayout => {
                if (JSON.stringify(prevLayout) !== JSON.stringify(adjustedLayout)) {
//                    console.log("[FormLayoutContext: fetchLatestLayout] Setting default layout.");
                    return adjustedLayout;
                }
//                console.log("[FormLayoutContext: fetchLatestLayout] Default layout matches current layoutConfig, skipping state update.");
                return prevLayout;
            });

            setLayoutModified(false);

            // Store default layout and metadata in session storage
            safeSetItem(localLayoutKey, adjustedLayout);
            safeSetItem(`${localLayoutKey}_hash`, null); // Default layout has no hash

//            console.log("[FormLayoutContext: fetchLatestLayout] Default layout saved to session storage.");
        } else {
            // Handle error cases
            console.error(`[FormLayoutContext: fetchLatestLayout] Error fetching layout: ${layoutResponse.message}`);
            setSaveError(layoutResponse.message);
        }
    } catch (error) {
        if (error.name === 'AbortError') {
            console.warn("[FormLayoutContext: fetchLatestLayout] Fetch aborted — likely due to a newer local edit.");
        } else {
            console.error("[FormLayoutContext: fetchLatestLayout] Failed to fetch layout due to error:", error);
            setSaveError(error.toString());
        }
    } finally {
        // Clear active request tracking
        activeFetchRequest.current = null;
    }
}, [formId, localLayoutKey, ensureMetadataForLayout, effectiveViewMode, abortActiveFetch]);


    useEffect(() => {
        if (!formIdReady || !formId) {
            console.warn("[FormLayoutContext: useEffect] formId is not ready, skipping fetchLatestLayout.");
            setIsLoading(true);
            return;
        }

        // Reset metadata enforcement when formId changes
        metadataEnsured.current = false;

        if (!activeFetchRequest.current) {
            const fetchTimeout = setTimeout(() => {
                fetchLatestLayout();
            }, 50);

            return () => clearTimeout(fetchTimeout);
        } else {
            console.warn("[FormLayoutContext: useEffect] Active fetch request detected. Skipping duplicate fetch.");
        }
    }, [formIdReady, formId, fetchLatestLayout]);

// useEffect to handle layout and metadata fetching from session storage, and fallbacks
useEffect(() => {
    if (!formIdReady || !formId) {
        console.warn("[FormLayoutContext: useEffect] Form ID is not ready yet, skipping fetch.");
        setIsLoading(true); // Stay in loading state until formId is available
        return; // Exit early if formId is not available or ready
    }

//    console.log("[FormLayoutContext: useEffect]  formId is ready. Checking for stored layout and metadata...");

    setIsSaving(true); // Indicate that a process of saving or loading is ongoing
    setIsLoading(true); // Start loading

    // First, attempt to fetch layout and metadata from session storage
    const storedLayout = safeGetItem(localLayoutKey);

    //  Prefer mode-aware metadata; fallback to legacy
    const storedFieldsMetadata =
      safeGetItem(localFieldsMetadataKey) ||
      safeGetItem(legacyFieldsMetadataKey);

    const storedHash = safeGetItem(`${localLayoutKey}_hash`);


    if (storedLayout && storedFieldsMetadata) {
//        console.log("[FormLayoutContext: useEffect] Found layout and metadata in storage:", { storedLayout, storedFieldsMetadata });

        const currentHash = generateHash(storedLayout);
//        console.log("[FormLayoutContext: useEffect] Current layout hash generated:", currentHash, "Stored hash:", storedHash);

        // Only update if layout in session storage has changed
        if (currentHash !== storedHash) {
//            console.log("[FormLayoutContext: useEffect] Layout has changed. Updating state.");
            setLayoutConfig(storedLayout); // Set the layout from session storage

            // ────────────────────────────────────────────────────────
            // Builder/public-preview: may push stored metadata back
            // into FieldMetadataContext to hydrate builder UX.
            // Viewer (/forms/:formId): DO NOT overwrite metadata
            // from storage – FieldMetadataContext already handles
            // Firestore vs cache, and we don't want to re-apply
            // stale AUF metadata here.
            // ────────────────────────────────────────────────────────
            if (isBuilderRoute || isPublicPreviewMode) {
              // Update field metadata using updateFieldMetadata for each field
              Object.keys(storedFieldsMetadata).forEach((fieldId) => {
                  const fieldMetadata = storedFieldsMetadata[fieldId];
                  const currentFieldMetadata = fieldsMetadata[fieldId];

                  if (JSON.stringify(currentFieldMetadata) !== JSON.stringify(fieldMetadata)) {
//                    console.log(`[FormLayoutContext: useEffect] Updating metadata for field ${fieldId} from storage`);
                      updateFieldMetadata(fieldId, fieldMetadata); // Aligning with FieldMetadataContext
                  }
              });

              //  Reset metadataEnsured flag before calling ensureMetadataForLayout to avoid stale data
              metadataEnsured.current = false;
              ensureMetadataForLayout(storedLayout);
              metadataEnsured.current = true;
            }

            setLayoutModified(false); // Reset the modified flag
            setIsLoading(false); // Mark loading as complete
            setIsSaving(false); // Mark saving as complete
        } else {
//            console.log("[FormLayoutContext: useEffect] Layout in storage matches current layout. Skipping update.");
            setIsLoading(false); // Loading is complete
            setIsSaving(false); // Saving is complete
        }
    } else {

//        console.log("[FormLayoutContext: useEffect] No layout found in storage. Checking Firestore...");

        // Ensure activeFetchRequest prevents race conditions
        if (!activeFetchRequest.current) {
//            console.log("[FormLayoutContext: useEffect] Triggering fetchLatestLayout.");
            fetchLatestLayout();
        } else {
            console.warn("[FormLayoutContext: useEffect] Active fetch request detected. Skipping duplicate fetch.");
        }
    }



    // Listen for storage changes to handle cases when session storage is updated externally
    const handleStorageChange = (event) => {
        if (event.key === localLayoutKey) {
//            console.log("[FormLayoutContext] Detected storage change:", event.key);

            const newLayout = safeGetItem(localLayoutKey);
            const newHash = generateHash(newLayout);

            // ✅ NEW: always read the latest stored hash at event time (prevents stale closure bugs)
            const latestStoredHash = safeGetItem(`${localLayoutKey}_hash`);

            if (newLayout && newHash !== latestStoredHash) {
//                console.log("[FormLayoutContext] Updating layout due to storage change:", newLayout);

                // ─────────────────────────────────────────────────────────────
                //  (Step 6): treat external storage updates as authoritative
                // and bump the edit epoch so in-flight Firestore hydration cannot
                // overwrite this newer local update.
                // ─────────────────────────────────────────────────────────────
                layoutEditEpoch.current += 1;
                console.log("[FormLayoutContext] layoutEditEpoch bumped due to storage event:", {
                    epochNow: layoutEditEpoch.current,
                    key: event.key,
                    formId,
                    localLayoutKey,
                    effectiveViewMode,
                });

                // ─────────────────────────────────────────────────────────────
                // cancel any in-flight Firestore hydrate so it cannot
                // overwrite this externally-updated local layout.
                // ─────────────────────────────────────────────────────────────
                abortActiveFetch('storageEvent');

                setLayoutConfig(newLayout);


            const newMetadata =
              safeGetItem(localFieldsMetadataKey) ||
              safeGetItem(legacyFieldsMetadataKey);

            if (newMetadata && (isBuilderRoute || isPublicPreviewMode)) {
//                    console.log("[FormLayoutContext] Found new fieldsMetadata from storage after change:", newMetadata);

                    // Iterate over newMetadata keys and update each field's metadata
                    Object.keys(newMetadata).forEach((fieldId) => {
                        const fieldMetadata = newMetadata[fieldId];
                        const currentFieldMetadata = fieldsMetadata[fieldId];

                        if (JSON.stringify(currentFieldMetadata) !== JSON.stringify(fieldMetadata)) {
//                            console.log(`[FormLayoutContext] Updating metadata for field ${fieldId} from storage change.`);
                            updateFieldMetadata(fieldId, fieldMetadata);
                        }
                    });
                } else if (!newMetadata) {
                    console.warn("[FormLayoutContext] No new fieldsMetadata found in storage after change.");
                }


                // ✅ Reset metadataEnsured before metadata sync – builder only
                if (isBuilderRoute || isPublicPreviewMode) {
                  metadataEnsured.current = false;
                  ensureMetadataForLayout(newLayout);
                  metadataEnsured.current = true;
                }

                setLayoutModified(false); // Reset the modified flag
            } else {
//                console.log("[FormLayoutContext] No changes detected in layout or hash after storage event.");
            }

        }
    };

    // Add an event listener for storage changes
    window.addEventListener('storage', handleStorageChange);


    // Clean up the event listener on component unmount
    return () => {
//        console.log("[FormLayoutContext] Cleaning up storage change listener.");
        window.removeEventListener('storage', handleStorageChange);
    };

}, [
  formIdReady,
  formId,
  localLayoutKey,
  localFieldsMetadataKey,
  legacyFieldsMetadataKey,
  ensureMetadataForLayout,
  fetchLatestLayout,
  fieldsMetadata,
  updateFieldMetadata,
  isBuilderRoute,
  isPublicPreviewMode,
  abortActiveFetch
]);



/* ────────────────────────────────────────────────────────────────
   AUF RESTORE (apply once per pending payload)
   - AUF-only: requires formDetails.isAssetUploadForm === true
   - Builder-only: runs only on /form-builder routes
   - Org+AUF guardrails: payload must match current org + current formId
   - Archived-only: payload is created only from archivedAufFields rows
   - Consumes the pending key so it does not re-apply on refresh
──────────────────────────────────────────────────────────────── */
useEffect(() => {
  // Hard gates: must be in builder, have formId, orgId, and the key
  if (!isBuilderRoute) return;
  if (!formIdReady || !formId) return;
  if (!organizationId || !AUF_RESTORE_KEY) return;

  // wait until we actually have a layout object to patch into
  if (!layoutConfig || Object.keys(layoutConfig).length === 0) return;

  // NEW: wait until FieldMetadataContext has finished hydrating,
  // otherwise restore can race with metadata hydration.
  if (fieldsLoading) return;

  // AUF-only gate (prevents normal forms from ever running restore)
  const isAUF = formDetails?.isAssetUploadForm === true;
  if (!isAUF) return;


  // Read pending restore payload (written by AssetStudioSettingsPage)
  const pending = safeGetItem(AUF_RESTORE_KEY);

  if (!pending || typeof pending !== 'object') return;

  // Validate payload shape + scope
  const pOrg  = pending.orgId || null;
  const pAuf  = pending.aufId || null;
  const row   = pending.archivedField || null;

  if (!pOrg || !pAuf || !row) {
    console.warn('[FormLayoutContext: AUF restore] pending payload malformed:', pending);
    // Consume malformed payload to prevent infinite loops
    safeRemoveItem(AUF_RESTORE_KEY);
    return;
  }

  // Guardrails: org + AUF id must match *this* builder session
  if (pOrg !== organizationId) {
    console.warn('[FormLayoutContext: AUF restore] org mismatch; ignoring.', {
      pendingOrg: pOrg,
      activeOrg : organizationId,
    });
    safeRemoveItem(AUF_RESTORE_KEY);
    return;
  }

  if (pAuf !== formId) {
    console.warn('[FormLayoutContext: AUF restore] aufId mismatch; ignoring.', {
      pendingAuf: pAuf,
      activeAuf : formId,
    });
    safeRemoveItem(AUF_RESTORE_KEY);
    return;
  }

  // Archived-only guard: must have a stable UFI + type
  const restoreUfi  = row?.ufi || null;
  const restoreType = row?.type || null;

  if (!restoreUfi || !restoreType) {
    console.warn('[FormLayoutContext: AUF restore] missing ufi/type; ignoring.', row);
    safeRemoveItem(AUF_RESTORE_KEY);
    return;
  }

  // If this UFI already exists in current metadata, do nothing (idempotent)
  const alreadyExists = Object.values(fieldsMetadata || {}).some((m) => m?.ufi === restoreUfi);
  if (alreadyExists) {
    console.log('[FormLayoutContext: AUF restore] UFI already present; consuming pending payload.', {
      ufi: restoreUfi,
      formId,
    });
    safeRemoveItem(AUF_RESTORE_KEY);
    return;
  }

  console.log('[FormLayoutContext: AUF restore] Applying archived field restore:', {
    organizationId,
    formId,
    ufi: restoreUfi,
    type: restoreType,
    label: row?.label || null,
    optionsSetUFI: row?.optionsSetUFI || null,
    hasOptionsSnapshot: Array.isArray(row?.optionsSnapshot) && row.optionsSnapshot.length > 0,
  });

  try {
    // 1) Create a new fieldId, but preserve the original UFI.
    const newFieldId = uuidv4();

    // ─────────────────────────────────────────────────────────────
    // AUF restore guard: register this fieldId so ensureMetadataForLayout
    // won’t try to "re-create" it with defaults during this pass.
    // Always clean up in finally, even if something throws.
    // ─────────────────────────────────────────────────────────────
    restoredFieldIds.current.add(newFieldId);

    try {
      // 2) Build metadata using your canonical defaults, but force UFI + optionsSetUFI
      const defaults = getDefaultFieldMetadata(
        newFieldId,
        restoreUfi,                         // preserve archived UFI
        restoreType,
        row?.optionsSetUFI || null          // preserve optionsSetUFI when present
      );

      const baseMeta = (defaults && defaults[newFieldId]) ? defaults[newFieldId] : {};

      const restoredMeta = {
        ...baseMeta,
        id: newFieldId,
        ufi: restoreUfi,
        type: baseMeta.type || restoreType,
        label: row?.label || baseMeta.label || 'Restored field',
        // carry AUF-specific semantic markers if they were archived
        ...(row?.assetFieldRole ? { assetFieldRole: row.assetFieldRole } : {}),
        ...(row?.semanticKey   ? { semanticKey: row.semanticKey } : {}),
        ...(row?.optionsSetUFI ? { optionsSetUFI: row.optionsSetUFI } : {}),
      };

      // 3) Restore dropdown options set (optionIds) when snapshot exists
      // NOTE: this writes to storage so MultiAnswerFieldContext can see it.
      if (
        (restoredMeta.type === 'single-select' || restoredMeta.type === 'multi-select') &&
        restoredMeta.optionsSetUFI &&
        Array.isArray(row?.optionsSnapshot) &&
        row.optionsSnapshot.length > 0
      ) {
        const ddKey = `form_${formId}_dropdownOptions`;
        const existing = safeGetItem(ddKey) || {};
        const next = { ...(existing || {}) };

        // Only set if missing (don’t overwrite live edits)
        if (!Array.isArray(next[restoredMeta.optionsSetUFI])) {
          next[restoredMeta.optionsSetUFI] = row.optionsSnapshot;

          console.log('[FormLayoutContext: AUF restore] Restored dropdownOptions snapshot:', {
            optionsSetUFI: restoredMeta.optionsSetUFI,
            count: row.optionsSnapshot.length,
          });

          safeSetItem(ddKey, next);
        } else {
          console.log('[FormLayoutContext: AUF restore] dropdownOptions already exist; leaving as-is:', {
            optionsSetUFI: restoredMeta.optionsSetUFI,
            count: next[restoredMeta.optionsSetUFI].length,
          });
        }
      }

      // 4) Insert the field into layout (safe default: append to row0)
      const nextLayout = { ...(layoutConfig || {}) };
      if (!Array.isArray(nextLayout.row0)) nextLayout.row0 = [];

      const idx = nextLayout.row0.length;

      const restoredFieldForLayout = {
        id: newFieldId,
        ufi: restoreUfi,
        type: restoredMeta.type,
        label: restoredMeta.label,
        placeholder: restoredMeta.placeholder,
        required: !!restoredMeta.required,
        index: idx,
        // keep optionsSetUFI on the layout object too (helps some UIs)
        ...(restoredMeta.optionsSetUFI ? { optionsSetUFI: restoredMeta.optionsSetUFI } : {}),
      };

      // Keep header parity (avoid stale inline style)
      if (restoredFieldForLayout.type === 'header') {
        restoredFieldForLayout.headerStyle = restoredMeta.headerStyle || undefined;
        restoredFieldForLayout.subheaderStyle = restoredMeta.subheaderStyle || undefined;
        delete restoredFieldForLayout.style;
      }

      nextLayout.row0 = [...nextLayout.row0, restoredFieldForLayout];

      // Always keep submitRow sane
      const adjusted = adjustSubmitRow(nextLayout);

      // Commit metadata FIRST so ensureMetadataForLayout doesn't treat this field as missing
      // and fire addFieldMetadata() with defaults that overwrite your restored label/settings.
      updateFieldMetadata(newFieldId, restoredMeta);

      // Commit layout to state + storage
      setLayoutConfig(adjusted);
      safeSetItem(localLayoutKey, adjusted);
      safeSetItem(`${localLayoutKey}_hash`, generateHash(adjusted));
      setLayoutModified(true);

      // Make sure the rest of the builder can render immediately
      metadataEnsured.current = false;
      ensureMetadataForLayout(adjusted);
      metadataEnsured.current = true;

        console.log('[FormLayoutContext: AUF restore] Restore applied successfully:', {
          newFieldId,
          ufi: restoreUfi,
          type: restoredMeta.type,
        });

        //  NEW: queue one-time restore success dialog for the builder route
        try {
          const successKey = organizationId ? `org_${organizationId}_aufRestoreSuccess` : null;

          if (successKey) {
            safeSetItem(successKey, {
              orgId: organizationId,
              aufId: formId,
              restoredAt: new Date().toISOString(),

              // what the dialog should display
              fieldLabel: row?.label || restoredMeta?.label || 'Restored field',
              fieldType: restoreType || restoredMeta?.type || null,
              optionsSetUFI: row?.optionsSetUFI || restoredMeta?.optionsSetUFI || null,

              // extra details for messaging
              notes: {
                preserved: ['UFI', 'Dropdown option IDs'],
                notPreserved: ['All field settings (placeholder, validation, etc.)'],
                requiresPublish: true,
              },
            });

            console.log('[FormLayoutContext: AUF restore] Queued AUF restore success dialog payload:', {
              successKey,
              aufId: formId,
              fieldLabel: row?.label || restoredMeta?.label,
            });
          }
        } catch (e) {
          console.warn('[FormLayoutContext: AUF restore] Failed to queue restore success dialog payload (non-fatal):', e?.message || e);
        }



      // ─────────────────────────────────────────────────────────────
      //  Option A: mark this archivedAufFields entry as restored
      // - keeps history
      // - lets the Settings page hide restored rows
      // - AUF-only (this effect is already AUF-gated)
      // - best-effort + non-blocking
      // ─────────────────────────────────────────────────────────────
      try {
        const restoredAtIso = new Date().toISOString();
        const restoredByUid = currentUser?.uid || null;

        // Run async so we never block the builder paint
        (async () => {
          try {
            const formRef = doc(db, 'forms', String(formId));
            const snap = await getDoc(formRef);

            if (!snap.exists()) {
              console.warn('[FormLayoutContext: AUF restore] Could not mark restored: form doc missing.', { formId });
              return;
            }

            const data = snap.data() || {};
            const archived = Array.isArray(data.archivedAufFields) ? data.archivedAufFields : [];

            // Match strategy:
            // 1) UFI must match
            // 2) If archivedAt exists on the pending row, require exact archivedAt match
            // 3) Only mark the first non-restored match
            const targetArchivedAt = row?.archivedAt || null;

            let marked = false;
            const nextArchived = archived.map((r) => {
              if (marked) return r;
              if (!r || r.ufi !== restoreUfi) return r;
              if (r.restoredAt) return r;

              if (targetArchivedAt && r.archivedAt !== targetArchivedAt) return r;

              marked = true;
              return {
                ...r,
                restoredAt: restoredAtIso,
                restoredBy: restoredByUid,
                restoredFieldId: newFieldId,
                restoredPendingPublish: true,
              };
            });

            console.log('[FormLayoutContext: AUF restore] Mark restored result:', {
              formId,
              restoreUfi,
              targetArchivedAt,
              marked,
            });

            if (!marked) {
              console.warn('[FormLayoutContext: AUF restore] No archivedAufFields entry matched to mark restored (non-fatal).', {
                formId,
                restoreUfi,
                targetArchivedAt,
                archivedCount: archived.length,
              });
              return;
            }

            await updateDoc(formRef, {
              archivedAufFields: nextArchived,
              lastModified: serverTimestamp(),
              ...(restoredByUid ? { lastModifiedBy: restoredByUid } : {}),
            });

            console.log('[FormLayoutContext: AUF restore] archivedAufFields updated with restoredAt/restoredFieldId.');
          } catch (e) {
            console.warn('[FormLayoutContext: AUF restore] Failed to mark restored (non-fatal):', e?.message || e);
          }
        })();
      } catch (e) {
        console.warn('[FormLayoutContext: AUF restore] Failed to start mark-restored job (non-fatal):', e?.message || e);
      }

    } finally {
      // AUF restore guard: cleanup so future ensures behave normally
      restoredFieldIds.current.delete(newFieldId);
    }

  } catch (e) {
    console.error('[FormLayoutContext: AUF restore] Restore failed:', e?.message || e);
  } finally {
    // Consume the pending payload so it won’t re-apply
    safeRemoveItem(AUF_RESTORE_KEY);
    console.log('[FormLayoutContext: AUF restore] Consumed pending restore payload key:', AUF_RESTORE_KEY);
  }

}, [
  isBuilderRoute,
  formIdReady,
  formId,
  formDetails,
  organizationId,
  currentUser,
  AUF_RESTORE_KEY,
  layoutConfig,
  fieldsMetadata,
  fieldsLoading,
  localLayoutKey,
  updateFieldMetadata,
  ensureMetadataForLayout,
]);



// useEffect to handle layout and metadata saving to storage
useEffect(() => {
    if (!formIdReady || !formId) {
        console.warn("[FormLayoutContext] formId is not available, skipping layout and metadata save.");
        return;
    }

//    console.log("[FormLayoutContext: useEffect] Checking if layout and metadata should be saved...");

    if (layoutModified && Object.keys(layoutConfig).length > 0 && Object.keys(fieldsMetadata).length > 0) {
//        console.log("[FormLayoutContext] Detected changes, proceeding to save layout and metadata to storage.");

        //  Step 1: Correct `submitRow` structure before saving
        const adjustedLayout = adjustSubmitRow(layoutConfig);

        //  Step 2: Reset metadataEnsured before saving to ensure metadata consistency
        metadataEnsured.current = false;

        //  Step 3: Generate and store hash of the adjusted layoutConfig before saving
        const layoutHash = generateHash(adjustedLayout);
//        console.log("[FormLayoutContext] Generated layout hash:", layoutHash);

        //  Step 4: Save layout config and metadata
//        console.log("[FormLayoutContext] Saving layoutConfig and layout hash to storage.");
        safeSetItem(localLayoutKey, adjustedLayout);
//        console.log(`[FormLayoutContext] Saved adjusted layoutConfig to storage with key ${localLayoutKey}:`, adjustedLayout);

        safeSetItem(`${localLayoutKey}_hash`, layoutHash);
//        console.log(`[FormLayoutContext] Saved layout hash to storage with key ${localLayoutKey}_hash:`, layoutHash);

        //  Save to mode-aware key so FormLayoutContext & FieldMetadataContext agree
        const fieldsMetadataKey = localFieldsMetadataKey;
        //        console.log(`[FormLayoutContext] Saving fieldsMetadata to storage with key ${fieldsMetadataKey}:`, fieldsMetadata);
        safeSetItem(fieldsMetadataKey, fieldsMetadata);

        // Optional: keep legacy key in sync during migration (safe, but not required long-term)
        safeSetItem(legacyFieldsMetadataKey, fieldsMetadata);

        //  Step 5: Reset modification flag and metadataEnsured tracking
        setLayoutModified(false);
        metadataEnsured.current = true; // Ensures future metadata sync logic skips redundant checks
//        console.log("[FormLayoutContext] Reset layoutModified flag and marked metadataEnsured as true.");
    } else {
//        console.log("[FormLayoutContext] Layout or fieldsMetadata not ready, skipping save.");
    }
}, [
  formIdReady,
  formId,
  layoutConfig,
  fieldsMetadata,
  layoutModified,
  localLayoutKey,
  localFieldsMetadataKey,
  legacyFieldsMetadataKey
]);

// Handle field change
const handleFieldChange = useCallback((fieldId, updates) => {
//    console.log(`[FormLayoutContext: handleFieldChange] Handling updates for field ID: ${fieldId}`, updates);

    //  Step 1: Prevent redundant updates by checking processedFields reference
    if (processedFields.current.has(fieldId)) {
        console.warn(`[FormLayoutContext: handleFieldChange] Skipping duplicate update for field ID: ${fieldId}`);
        return;
    }

    //  Step 2: Track ongoing metadata updates to avoid race conditions
    if (activeMetadataPromises.current?.has(fieldId) || activeMetadataTimeouts.current?.has(fieldId)) {
        console.warn(`[FormLayoutContext: handleFieldChange] Active metadata creation in progress for field ID: ${fieldId}. Skipping update.`);
        return;
    }

    //  Step 3: Update metadata and mark field as processed
    updateFieldMetadata(fieldId, updates);
    processedFields.current.add(fieldId);

//    console.log(`[FormLayoutContext: handleFieldChange] Metadata updated successfully for field ID: ${fieldId}`);
}, [updateFieldMetadata]);

const handleDrop = async (item, type, originalRowIndex, originalFieldIndex, adjustedRowIndex, adjustedFieldIndex) => {

//const handleDrop = (item, type, originalRowIndex, originalFieldIndex, adjustedRowIndex, adjustedFieldIndex) => {
    console.log("[FormLayoutContext: handleDrop] handleDrop called:", {
        item,
        type,
        originalRowIndex,
        originalFieldIndex,
        adjustedRowIndex,
        adjustedFieldIndex
    });


    if (!layoutConfig || item.id === undefined) {
        console.error("[FormLayoutContext: handleDrop] Error: Missing layout configuration or item ID is undefined.", { item, layoutConfig });
        return;
    }

    // Prevent handleDrop from running during initialization or without user interaction
    if (!item.isNew && !item.id) {
        console.warn("[FormLayoutContext: handleDrop] handleDrop called for an existing item during initialization. Skipping.");
        return;
    }

    // ─────────────────────────────────────────────────────────────
    //  Enterprise-grade: bump edit epoch ONLY after we know this
    // drop will actually mutate layout (prevents false epoch bumps
    // on early-return paths).
    // This prevents any in-flight Firestore hydration from overwriting
    // the user's dropped field.
    // ─────────────────────────────────────────────────────────────
    layoutEditEpoch.current += 1;
    console.log("[FormLayoutContext: handleDrop] layoutEditEpoch bumped:", {
        epochNow: layoutEditEpoch.current,
        formId,
        localLayoutKey,
        effectiveViewMode,
    });

    // ─────────────────────────────────────────────────────────────
    // cancel any in-flight Firestore hydrate so it cannot
    // overwrite this drop if it finishes later.
    // ─────────────────────────────────────────────────────────────
    abortActiveFetch('handleDrop');

    // Ensure the row and field indices are defined; initialize if necessary
    adjustedRowIndex = adjustedRowIndex !== undefined ? adjustedRowIndex : originalRowIndex;

    adjustedFieldIndex = adjustedFieldIndex !== undefined ? adjustedFieldIndex : originalFieldIndex;

    let newLayoutConfig = { ...layoutConfig };

    // Handling the ABOVE drop
    if (type === 'above') {
//        console.log("[handleDrop] ABOVE drop detected. Preparing to create new row0 and shift existing rows.");

        // Sort existing row keys in descending order to shift them down safely
        const sortedKeys = Object.keys(newLayoutConfig)
            .filter(key => key.startsWith('row'))
            .sort((a, b) => parseInt(b.replace('row', ''), 10) - parseInt(a.replace('row', ''), 10));

//        console.log("[handleDrop] Existing Rows Before Shift:", sortedKeys);

        // Shift existing rows down by 1
        sortedKeys.forEach(key => {
            const currentIndex = parseInt(key.replace('row', ''), 10);
            const newKey = `row${currentIndex + 1}`;

            newLayoutConfig[newKey] = newLayoutConfig[key];
            console.log(`[handleDrop] Shifted ${key} to ${newKey}`);
        });

        // Insert new empty row0
        newLayoutConfig['row0'] = [];
//        console.log("[handleDrop] Created new row0.");

        // Adjust drop position to the new row0
        adjustedRowIndex = 0;
        adjustedFieldIndex = 0;
    }

//  Handling the BELOW drop (Corrected: insert at adjustedRowIndex)
if (type === 'below') {
    // IMPORTANT:
    // DropZone already passes adjustedRowIndex as the *insertion row* for BELOW.
    // Example: dropping "below row1" yields adjustedRowIndex = 2.
    // Therefore, handleDrop must insert/create row{adjustedRowIndex} (NOT +1 again),
    // and shift existing rows at >= adjustedRowIndex down by 1.

    const sortedKeys = Object.keys(newLayoutConfig)
        .filter(key => key.startsWith('row'))
        .sort((a, b) => parseInt(a.replace('row', ''), 10) - parseInt(b.replace('row', ''), 10));

    //  Shift rows down starting from the last row whose numeric index >= adjustedRowIndex
    for (let i = sortedKeys.length - 1; i >= 0; i--) {
        const currentKey = sortedKeys[i];
        const currentIndex = parseInt(currentKey.replace('row', ''), 10);

        if (Number.isNaN(currentIndex)) continue;

        // Only shift rows at or below the insertion index
        if (currentIndex >= adjustedRowIndex) {
            const newKey = `row${currentIndex + 1}`;
            newLayoutConfig[newKey] = newLayoutConfig[currentKey];
//          console.log(`[handleDrop] ⬇️ Shifted ${currentKey} to ${newKey}`);
        }
    }

    //  Create the new row at the insertion index (NOT +1)
    const newRowKey = `row${adjustedRowIndex}`;
    newLayoutConfig[newRowKey] = [];

    // Insert at start of the new row
    adjustedFieldIndex = 0;
}



    const rowKey = `row${adjustedRowIndex}`;
    let newRow = newLayoutConfig[rowKey] ? [...newLayoutConfig[rowKey]] : [];

//  Handle LEFT drop to insert item at the start of the row
// IMPORTANT: If this is a NEW palette item (item.isNew === true),
// do NOT insert the raw drag object into layoutConfig (it may contain React elements / DOM refs).
// Let new items flow through the sanitizedItem.isNew path below so we generate a real fieldId/metadata.
if (type === 'left' && !item?.isNew) {
//    console.log("[handleDrop] LEFT drop detected.");

    //  Ensure adjustedRowIndex is valid (prevents negative indices)
    adjustedRowIndex = adjustedRowIndex < 0 ? 0 : adjustedRowIndex;

    const rowKey = `row${adjustedRowIndex}`;
    if (!newLayoutConfig[rowKey]) {
        newLayoutConfig[rowKey] = [];
    }

    let newRow = newLayoutConfig[rowKey];

    //  Clear any existing instance of the item
    Object.keys(newLayoutConfig).forEach(key => {
        const index = newLayoutConfig[key].findIndex(field => field.id === item.id);
        if (index > -1) {
            newLayoutConfig[key].splice(index, 1);
//            console.log(`[handleDrop] Removed existing item from ${key}`);
        }
    });

    //  Insert at the beginning (LEFT) of the correct row
    newRow.splice(0, 0, item);
//    console.log(`[handleDrop] Inserted item to the LEFT in row ${adjustedRowIndex}`);

    setLayoutConfig(newLayoutConfig);
    setLayoutModified(true);
    ensureMetadataForLayout(newLayoutConfig);
    return; //  Exit early after handling LEFT drop (existing items only)
}


    // Adjust indices for RIGHT drop and bounds
    if (!newLayoutConfig[rowKey]) {
        newLayoutConfig[rowKey] = [];
        newRow = newLayoutConfig[rowKey];
    }

    if (type === 'right' && adjustedFieldIndex > newRow.length) {
        adjustedFieldIndex = newRow.length;
    } else if (type === 'left' && adjustedFieldIndex < 0) {
        adjustedFieldIndex = 0;
    }


// Sanitize the item to avoid non-serializable fields
const sanitizedItem = { ...item };
delete sanitizedItem.children;
delete sanitizedItem.__reactFiber$;

//console.log("[handleDrop] Sanitized item:", sanitizedItem);

//  Handle new item addition or existing item repositioning
if (sanitizedItem.isNew) {

    // ─────────────────────────────────────────────────────────────
    // Managed field preview path
    // - Prefer the full managedFieldDefinition already attached to
    //   the drag item so we can build the FINAL managed field once
    //   and avoid placeholder flicker.
    // - Only fall back to placeholder + fetch if the definition is
    //   missing from the drag payload.
    // ─────────────────────────────────────────────────────────────
    if (isManagedFieldPreviewItem(sanitizedItem)) {
        try {
            if (!organizationId) {
                console.error(
                    '[FormLayoutContext: handleDrop] Cannot insert managed field preview: organizationId is missing.',
                    { item: sanitizedItem, formId }
                );
                return;
            }

            console.log('[FormLayoutContext: handleDrop] Managed field preview detected. Preparing managed insert...', {
                organizationId,
                formId,
                managedFieldId: sanitizedItem.managedFieldId,
                hasManagedFieldDefinition: !!sanitizedItem.managedFieldDefinition,
                adjustedRowIndex,
                adjustedFieldIndex,
            });

            // ─────────────────────────────────────────────────────────────
            // ENTERPRISE PRIMARY PATH
            // If the sidebar already passed the full definition, build the
            // FINAL managed field immediately and insert it once.
            // ─────────────────────────────────────────────────────────────
            if (sanitizedItem.managedFieldDefinition) {
                const managedFieldDefinition = sanitizedItem.managedFieldDefinition;

                const builtManagedInstance = buildManagedFieldInstance({
                    organizationId,
                    managedFieldDefinition,
                });

                const managedFieldId = builtManagedInstance.fieldId;
                const managedUfi = builtManagedInstance.ufi;
                const managedMetadata = {
                    ...builtManagedInstance.metadata,
                    isLoading: false,
                };
                const managedLayoutField = {
                    ...builtManagedInstance.layoutField,
                    index: adjustedFieldIndex,
                };

                console.log('[FormLayoutContext: handleDrop] Built managed field instance successfully.', {
                    managedFieldId: sanitizedItem.managedFieldId,
                    localFieldId: managedFieldId,
                    localUfi: managedUfi,
                    type: managedMetadata?.type,
                    optionsSetUFI: managedMetadata?.optionsSetUFI || null,
                    orgManaged: managedMetadata?.orgManaged === true,
                });

                // Seed managed dropdown options before first paint
                if (
                    managedMetadata?.orgManaged === true &&
                    (
                        managedMetadata?.type === 'single-select' ||
                        managedMetadata?.type === 'multi-select'
                    ) &&
                    managedMetadata?.optionsSetUFI
                ) {
                    try {
                        const managedRuntimeResult = await resolveManagedDropdownInstanceOptions({
                            organizationId,
                            fieldMetadata: managedMetadata,
                            now: new Date(),
                        });

                        if (
                            managedRuntimeResult?.status === 'success' &&
                            Array.isArray(managedRuntimeResult?.options)
                        ) {
                            const activeDropdownOptionsKey =
                                localDropdownOptionsKey || `form_${formId}_dropdownOptions__published`;
                            const activeDropdownLastUpdatedKey =
                                localDropdownLastUpdatedKey || `form_${formId}_dropdownLastUpdated__published`;

                            const legacyKey =
                                legacyDropdownOptionsKey || `form_${formId}_dropdownOptions`;
                            const legacyUpdatedKey =
                                legacyDropdownLastUpdatedKey || `form_${formId}_dropdownLastUpdated`;

                            const existingDropdownOptions =
                                safeGetItem(activeDropdownOptionsKey) ||
                                safeGetItem(legacyKey) ||
                                {};

                            const nextDropdownOptions = {
                                ...(existingDropdownOptions || {}),
                                [managedMetadata.optionsSetUFI]: managedRuntimeResult.options,
                            };

                            safeSetItem(activeDropdownOptionsKey, nextDropdownOptions);
                            safeSetItem(activeDropdownLastUpdatedKey, new Date().toISOString());

                            safeSetItem(legacyKey, nextDropdownOptions);
                            safeSetItem(legacyUpdatedKey, new Date().toISOString());

                            console.log('[FormLayoutContext: handleDrop] Seeded org-managed dropdown options for first paint:', {
                                organizationId,
                                formId,
                                managedFieldId: sanitizedItem.managedFieldId,
                                localFieldId: managedFieldId,
                                optionsSetUFI: managedMetadata.optionsSetUFI,
                                optionCount: managedRuntimeResult.options.length,
                            });
                        } else {
                            console.warn('[FormLayoutContext: handleDrop] Managed dropdown seed skipped: runtime resolver returned no options.', {
                                organizationId,
                                formId,
                                managedFieldId: sanitizedItem.managedFieldId,
                                localFieldId: managedFieldId,
                                optionsSetUFI: managedMetadata?.optionsSetUFI || null,
                                managedRuntimeResult,
                            });
                        }
                    } catch (seedError) {
                        console.warn('[FormLayoutContext: handleDrop] Failed to pre-seed org-managed dropdown options (non-fatal):', {
                            organizationId,
                            formId,
                            managedFieldId: sanitizedItem.managedFieldId,
                            localFieldId: managedFieldId,
                            optionsSetUFI: managedMetadata?.optionsSetUFI || null,
                            message: seedError?.message || seedError,
                        });
                    }
                }

                // Commit FINAL metadata immediately
                updateFieldMetadata(managedFieldId, managedMetadata);

                // Insert FINAL field immediately
                newRow.splice(adjustedFieldIndex, 0, managedLayoutField);
                newRow = newRow.map((f, i) => ({ ...f, index: i }));
                newLayoutConfig[rowKey] = newRow;

                Object.keys(newLayoutConfig).forEach(key => {
                    if (newLayoutConfig[key].length === 0) {
                        delete newLayoutConfig[key];
                        console.log(`[handleDrop] Deleted empty row: ${key}`);
                    }
                });

                try {
                    safeSetItem(localLayoutKey, newLayoutConfig);
                    safeSetItem(`${localLayoutKey}_hash`, generateHash(newLayoutConfig));
                    console.log(`[handleDrop] Successfully persisted layout data for formId: ${formId}`);
                } catch (error) {
                    console.error('[FormLayoutContext: handleDrop] Error persisting managed-field layout to storage:', error);
                }

                setLayoutConfig(newLayoutConfig);
                setLayoutModified(true);
                ensureMetadataForLayout(newLayoutConfig);
                return;
            }

            // ─────────────────────────────────────────────────────────────
            // FALLBACK PATH
            // Only used if managedFieldDefinition is missing on the drag item.
            // Keep your existing placeholder + fetch flow here.
            // ─────────────────────────────────────────────────────────────
            console.log('[FormLayoutContext: handleDrop] managedFieldDefinition missing on drag item. Falling back to placeholder + fetch flow...', {
                organizationId,
                formId,
                managedFieldId: sanitizedItem.managedFieldId,
            });

            const tempManagedFieldId = uuidv4();
            const tempManagedUfi = uuidv4();

            const tempManagedMetadata = {
                id: tempManagedFieldId,
                ufi: tempManagedUfi,
                type: 'single-select', // safe placeholder; overwritten by final metadata
                label: 'Loading managed field...',
                placeholder: 'Configuring managed field...',
                required: false,
                isLoading: true,

                // managed-field identity
                orgManaged: true,
                managedFieldId: sanitizedItem.managedFieldId,
                organizationId,
            };

            const tempManagedLayoutField = {
                id: tempManagedFieldId,
                ufi: tempManagedUfi,
                type: 'single-select',
                label: 'Loading managed field...',
                placeholder: 'Configuring managed field...',
                required: false,
                index: adjustedFieldIndex,
            };

            // Commit placeholder metadata immediately
            updateFieldMetadata(tempManagedFieldId, tempManagedMetadata);

            // Insert placeholder field immediately into layout
            newRow.splice(adjustedFieldIndex, 0, tempManagedLayoutField);
            newRow = newRow.map((f, i) => ({ ...f, index: i }));
            newLayoutConfig[rowKey] = newRow;

            Object.keys(newLayoutConfig).forEach(key => {
                if (newLayoutConfig[key].length === 0) {
                    delete newLayoutConfig[key];
                    console.log(`[handleDrop] Deleted empty row: ${key}`);
                }
            });

            try {
                safeSetItem(localLayoutKey, newLayoutConfig);
                safeSetItem(`${localLayoutKey}_hash`, generateHash(newLayoutConfig));
                console.log(`[handleDrop] Successfully persisted layout data for formId: ${formId}`);
            } catch (error) {
                console.error('[FormLayoutContext: handleDrop] Error persisting managed-field placeholder layout to storage:', error);
            }

            setLayoutConfig(newLayoutConfig);
            setLayoutModified(true);
            ensureMetadataForLayout(newLayoutConfig);

            // Prefer an already-attached definition if the future UI provides it,
            // otherwise fetch from the org-managed registry.
            let managedFieldDefinition = sanitizedItem.managedFieldDefinition || null;

            if (!managedFieldDefinition) {
                const managedFieldResponse = await getManagedFieldById({
                    organizationId,
                    managedFieldId: sanitizedItem.managedFieldId,
                });

                if (managedFieldResponse.status !== 'success' || !managedFieldResponse.data) {
                    console.error(
                        '[FormLayoutContext: handleDrop] Failed to resolve managed field definition for preview item.',
                        {
                            organizationId,
                            formId,
                            managedFieldId: sanitizedItem.managedFieldId,
                            managedFieldResponse,
                        }
                    );

                    // Clean up placeholder if resolution fails
                    deleteField(tempManagedFieldId);
                    return;
                }

                managedFieldDefinition = managedFieldResponse.data;
            }

            // IMPORTANT:
            // Reuse the SAME local fieldId + ufi so the inserted placeholder is
            // upgraded in place instead of being replaced by a second field.
            const builtManagedInstance = buildManagedFieldInstance({
                organizationId,
                managedFieldDefinition,
                fieldId: tempManagedFieldId,
                ufi: tempManagedUfi,
            });

            const managedFieldId = builtManagedInstance.fieldId;
            const managedUfi = builtManagedInstance.ufi;
            const managedMetadata = {
                ...builtManagedInstance.metadata,
                isLoading: false,
            };
            const managedLayoutField = {
                ...builtManagedInstance.layoutField,
                index: adjustedFieldIndex,
            };

            console.log('[FormLayoutContext: handleDrop] Built managed field instance successfully.', {
                managedFieldId: sanitizedItem.managedFieldId,
                localFieldId: managedFieldId,
                localUfi: managedUfi,
                type: managedMetadata?.type,
                optionsSetUFI: managedMetadata?.optionsSetUFI || null,
                orgManaged: managedMetadata?.orgManaged === true,
            });

            // ─────────────────────────────────────────────────────────────
            // Enterprise-grade first-paint seeding for org-managed dropdowns
            //
            // PURPOSE
            // - Ensure the already-inserted managed field upgrades with the
            //   correct options as soon as possible, without changing ownership
            //   of the centrally governed option set.
            // ─────────────────────────────────────────────────────────────
            if (
                managedMetadata?.orgManaged === true &&
                (
                    managedMetadata?.type === 'single-select' ||
                    managedMetadata?.type === 'multi-select'
                ) &&
                managedMetadata?.optionsSetUFI
            ) {
                try {
                    const managedRuntimeResult = await resolveManagedDropdownInstanceOptions({
                        organizationId,
                        fieldMetadata: managedMetadata,
                        now: new Date(),
                    });

                    if (
                        managedRuntimeResult?.status === 'success' &&
                        Array.isArray(managedRuntimeResult?.options)
                    ) {
                        const activeDropdownOptionsKey =
                            localDropdownOptionsKey || `form_${formId}_dropdownOptions__published`;
                        const activeDropdownLastUpdatedKey =
                            localDropdownLastUpdatedKey || `form_${formId}_dropdownLastUpdated__published`;

                        const legacyKey =
                            legacyDropdownOptionsKey || `form_${formId}_dropdownOptions`;
                        const legacyUpdatedKey =
                            legacyDropdownLastUpdatedKey || `form_${formId}_dropdownLastUpdated`;

                        const existingDropdownOptions =
                            safeGetItem(activeDropdownOptionsKey) ||
                            safeGetItem(legacyKey) ||
                            {};

                        const nextDropdownOptions = {
                            ...(existingDropdownOptions || {}),
                            [managedMetadata.optionsSetUFI]: managedRuntimeResult.options,
                        };

                        safeSetItem(activeDropdownOptionsKey, nextDropdownOptions);
                        safeSetItem(activeDropdownLastUpdatedKey, new Date().toISOString());

                        safeSetItem(legacyKey, nextDropdownOptions);
                        safeSetItem(legacyUpdatedKey, new Date().toISOString());

                        console.log('[FormLayoutContext: handleDrop] Seeded org-managed dropdown options for first paint:', {
                            organizationId,
                            formId,
                            managedFieldId: sanitizedItem.managedFieldId,
                            localFieldId: managedFieldId,
                            optionsSetUFI: managedMetadata.optionsSetUFI,
                            optionCount: managedRuntimeResult.options.length,
                        });
                    } else {
                        console.warn('[FormLayoutContext: handleDrop] Managed dropdown seed skipped: runtime resolver returned no options.', {
                            organizationId,
                            formId,
                            managedFieldId: sanitizedItem.managedFieldId,
                            localFieldId: managedFieldId,
                            optionsSetUFI: managedMetadata?.optionsSetUFI || null,
                            managedRuntimeResult,
                        });
                    }
                } catch (seedError) {
                    console.warn('[FormLayoutContext: handleDrop] Failed to pre-seed org-managed dropdown options (non-fatal):', {
                        organizationId,
                        formId,
                        managedFieldId: sanitizedItem.managedFieldId,
                        localFieldId: managedFieldId,
                        optionsSetUFI: managedMetadata?.optionsSetUFI || null,
                        message: seedError?.message || seedError,
                    });
                }
            }

            // Upgrade the same field metadata in place
            updateFieldMetadata(managedFieldId, managedMetadata);

            // Upgrade the same layout field in place
            const upgradedRow = (newLayoutConfig[rowKey] || []).map((field, index) => {
                if (field?.id !== managedFieldId) {
                    return { ...field, index };
                }

                return {
                    ...managedLayoutField,
                    index,
                };
            });

            newLayoutConfig[rowKey] = upgradedRow;

            try {
                safeSetItem(localLayoutKey, newLayoutConfig);
                safeSetItem(`${localLayoutKey}_hash`, generateHash(newLayoutConfig));
                console.log(`[handleDrop] Successfully persisted layout data for formId: ${formId}`);
            } catch (error) {
                console.error('[FormLayoutContext: handleDrop] Error persisting managed-field upgraded layout to storage:', error);
            }

            setLayoutConfig({ ...newLayoutConfig });
            setLayoutModified(true);
            ensureMetadataForLayout(newLayoutConfig);

            // IMPORTANT:
            // Return here so we do NOT continue into the generic local-field path.
            return;

        } catch (error) {
            console.error('[FormLayoutContext: handleDrop] Error inserting managed field preview item:', error);
            setSaveError(error?.message || String(error));
            return;
        }
    }

    const newFieldId = uuidv4();  //  Generate new field ID
    const newUFI = uuidv4();      //  Generate a UFI for immediate layout insertion

    sanitizedItem.id = newFieldId;
    sanitizedItem.isNew = false;

//    console.log(`[handleDrop] Adding new field with id: ${newFieldId} and UFI: ${newUFI}`);

    //  TEMPORARY METADATA: Display instant metadata during async metadata creation
    const tempMetadata = {
        id: newFieldId,
        ufi: newUFI,         //  Temporary UFI to ensure layout renders immediately
        type: sanitizedItem.type,
        label: `Loading ${sanitizedItem.type}...`,
        placeholder: `Configuring ${sanitizedItem.type}...`,
        required: false,
        isLoading: true      //  NEW: Flag to identify in-progress metadata
    };

//    console.log(`[handleDrop] Adding temporary metadata for field ID: ${newFieldId}`, tempMetadata);

    //  Add temporary metadata to FieldMetadataContext
    updateFieldMetadata(newFieldId, tempMetadata); //  Uses correct context logic

    // Retrieve default metadata for inserting into layout
    const defaultMetadata = getDefaultFieldMetadata(newFieldId, newUFI, sanitizedItem.type);
    const newFieldMetadata = {
        ...defaultMetadata[newFieldId],
        id: newFieldId,
        ufi: newUFI,
        index: adjustedFieldIndex,
        type: sanitizedItem.type,
    };

    // [FIX: Remove textAlign from new header fields so layout won't store alignment]
    const newField = { ...sanitizedItem, ...newFieldMetadata };
    if (newField.type === 'header') {
        newField.headerStyle = {
            ...newFieldMetadata.headerStyle
        };
        newField.subheaderStyle = {
            ...newFieldMetadata.subheaderStyle
        };

        // Clean any outdated inline `style` attributes from the layout
        delete newField.style;
    }

    //  Insert new field into layout IMMEDIATELY (no await before persist)
    newRow.splice(adjustedFieldIndex, 0, newField);
    // console.log("[handleDrop] Added new field to layout at row:", adjustedRowIndex, "and index:", adjustedFieldIndex);

    //  Assign updated row data
    newLayoutConfig[rowKey] = newRow;

    //  Clean up any empty rows
    Object.keys(newLayoutConfig).forEach(key => {
        if (newLayoutConfig[key].length === 0) {
            delete newLayoutConfig[key];
            console.log(`[handleDrop] Deleted empty row: ${key}`);
        }
    });

    //  Persist layout EARLY (enterprise-grade: prevents “storage missing → Firestore hydrate → overwrite” flicker)
    try {
        // IMPORTANT: safeSetItem already stringifies internally.
        // Store the object, NOT a JSON string, so safeGetItem returns an object later.
        safeSetItem(localLayoutKey, newLayoutConfig);                         //  same key, just consistent
        safeSetItem(`${localLayoutKey}_hash`, generateHash(newLayoutConfig)); //  NEW: keep hash in sync
        console.log(`[handleDrop] Successfully persisted layout data for formId: ${formId}`);
    } catch (error) {
        console.error("[FormLayoutContext: handleDrop] Error persisting layout to storage:", error);
    }

    //  Apply state updates EARLY (so UI stays stable even while metadata finishes)
    setLayoutConfig(newLayoutConfig);
    setLayoutModified(true);
    ensureMetadataForLayout(newLayoutConfig);

    //  Start async metadata creation in the background (NO await)
    // Keep your original safety pattern: timeout buffer + cleanup + failure cleanup.
    try {
        // Prevent duplicate metadata creation requests
if (activeMetadataPromises.current?.has(newFieldId) || activeMetadataTimeouts.current?.has(newFieldId)) {
            console.warn(`[handleDrop] Active metadata creation already in progress for field ID: ${newFieldId}. Skipping duplicate request.`);
        } else {
            const metadataPromise = Promise.race([
                addFieldMetadata(newFieldId, sanitizedItem.type, newUFI),
                new Promise((resolve) => setTimeout(resolve, 3000)) // Buffer delay for slow network conditions
            ]);

            // Track the in-flight creation so other logic won’t collide
            activeMetadataPromises.current.set(newFieldId, metadataPromise);

            metadataPromise
                .then(() => {
                    const cleanupTimeout = setTimeout(() => {
                        //  Remove the `isLoading` flag from metadata to finalize its state
                        updateFieldMetadata(newFieldId, { isLoading: false });

                        //  Ensure failed entries don't linger
                        activeMetadataPromises.current.delete(newFieldId);
                        activeMetadataTimeouts.current.delete(newFieldId);

//                        console.log(`[handleDrop]  Temporary metadata cleaned up for field ID: ${newFieldId}`);
                    }, 2000);

                    // Store the timeout reference (keeps your original pattern intact)
                    activeMetadataTimeouts.current.set(newFieldId, cleanupTimeout);
                })
                .catch((error) => {
                    console.error(`[handleDrop] ❗ Error adding metadata for field ID: ${newFieldId}`, error);

                    //  Cleanup on failure: Remove the temporary metadata to avoid stale data
                    deleteFieldMetadata(newFieldId);  // Correctly removes failed entries from state and storage

                    //  Ensure failed entries don't linger (Promise/timeout maps)
                    activeMetadataPromises.current.delete(newFieldId);
                    activeMetadataTimeouts.current.delete(newFieldId);
                });
        }
    } catch (error) {
        console.error(`[handleDrop] ❗ Error scheduling metadata creation for field ID: ${newFieldId}`, error);

        //  Cleanup on failure: Remove the temporary metadata to avoid stale data
        deleteFieldMetadata(newFieldId);  // Correctly removes failed entries from state and storage

        // Ensure failed entries don't linger (Promise/timeout maps)
        activeMetadataPromises.current.delete(newFieldId);
        activeMetadataTimeouts.current.delete(newFieldId);
    }

    // IMPORTANT: return so we don’t re-run the generic persist/state block below
    // (we already persisted + set state above in the new-item path)
    return;


} else {
    //  Handle repositioning of existing items
    //  ENTERPRISE: remove first, rebuild target row, adjust index when moving right within same row

    let movedField = null;
    let removedFromRowIndex = null;
    let removedFromFieldIndex = null;

    Object.keys(newLayoutConfig).forEach((key) => {
        const arr = newLayoutConfig[key];
        if (!Array.isArray(arr)) return;

        const idx = arr.findIndex((f) => f?.id === sanitizedItem.id);
        if (idx > -1) {
            movedField = arr[idx];
            removedFromRowIndex = parseInt(String(key).replace('row', ''), 10);
            removedFromFieldIndex = idx;

            newLayoutConfig[key] = [
                ...arr.slice(0, idx),
                ...arr.slice(idx + 1),
            ];
        }
    });

    if (
        removedFromRowIndex === adjustedRowIndex &&
        typeof removedFromFieldIndex === 'number' &&
        typeof adjustedFieldIndex === 'number' &&
        removedFromFieldIndex < adjustedFieldIndex
    ) {
        adjustedFieldIndex = Math.max(0, adjustedFieldIndex - 1);
    }

    newRow = newLayoutConfig[rowKey] ? [...newLayoutConfig[rowKey]] : [];

    const fieldToInsert = movedField ? { ...movedField } : { ...sanitizedItem };

    if (adjustedFieldIndex > newRow.length) adjustedFieldIndex = newRow.length;
    if (adjustedFieldIndex < 0) adjustedFieldIndex = 0;

    newRow.splice(adjustedFieldIndex, 0, fieldToInsert);
    newRow = newRow.map((f, i) => ({ ...f, index: i }));
}

//  Assign updated row data
newLayoutConfig[rowKey] = newRow;

// Clean up any empty rows
Object.keys(newLayoutConfig).forEach(key => {
    if (newLayoutConfig[key].length === 0) {
        delete newLayoutConfig[key];
        console.log(`[handleDrop] Deleted empty row: ${key}`);
    }
});

// **Circular Reference Check**
try {
    JSON.stringify(newLayoutConfig);
} catch (error) {
    console.error("[FormLayoutContext: handleDrop] Circular structure detected in newLayoutConfig:", error);
}

// Persisting layout to sessionStorage and localStorage
try {
    // IMPORTANT: safeSetItem already stringifies internally.
    // Store the object, NOT a JSON string, so safeGetItem returns an object later.
    safeSetItem(localLayoutKey, newLayoutConfig);                         // same key, just consistent
    safeSetItem(`${localLayoutKey}_hash`, generateHash(newLayoutConfig)); //  keep hash in sync
    console.log(`[handleDrop] Successfully persisted layout data for formId: ${formId}`);
} catch (error) {
    console.error("[FormLayoutContext: handleDrop] Error persisting layout to storage:", error);
}


// Final updates for state and metadata
setLayoutConfig(newLayoutConfig);
setLayoutModified(true);
ensureMetadataForLayout(newLayoutConfig);

//console.log("[FormLayoutContext: handleDrop] Metadata and Layout updates completed successfully.");
};


// Drag start and end handlers
const handleDragStart = (dragId = null) => {
    console.log('[FormLayoutContext] handleDragStart → arm dropzones', { dragId });
    setActiveDragId(dragId || null);
    setShowDropZones(true);
};

const handleDragEnd = () => {
    console.log('[FormLayoutContext] handleDragEnd → disarm dropzones');
    setActiveDragId(null);
    setShowDropZones(false);
};


const saveLayout = async (isDraft = true) => {
    if (!formId || !layoutConfig) {
        console.error(`[FormLayoutContext] Cannot ${isDraft ? 'save draft' : 'publish'}: Missing formId or layoutConfig`);
        return;
    }

    console.log(`[FormLayoutContext: saveLayout] Preparing to ${isDraft ? 'save draft' : 'publish'} layout...`);
    setIsSaving(true);

    try {
        //  Correct `submitRow` structure before saving
        const adjustedLayout = adjustSubmitRow(layoutConfig);

        //  Ensure metadata alignment before saving
        console.log("[FormLayoutContext: saveLayout] Ensuring metadata alignment before saving...");
        metadataEnsured.current = false;
        ensureMetadataForLayout(adjustedLayout);
        metadataEnsured.current = true;

        //  Remove temporary metadata (if any)
        const storedMetadata =
          safeGetItem(localFieldsMetadataKey) ||
          safeGetItem(legacyFieldsMetadataKey) ||
          {};
        Object.keys(storedMetadata).forEach(fieldId => {
            if (storedMetadata[fieldId]?.isLoading) {
                console.warn(`[FormLayoutContext: saveLayout] Removing incomplete metadata for field ID: ${fieldId}`);
                delete storedMetadata[fieldId];
                deleteFieldMetadata(fieldId);
            }
        });
        safeSetItem(localFieldsMetadataKey, fieldsMetadata);
        safeSetItem(legacyFieldsMetadataKey, fieldsMetadata); // optional migration


        //  Save adjusted layout
        const additionalMetadata = { isDraft };
        const response = await saveLayoutConfig(formId, adjustedLayout, additionalMetadata);

        if (response.status === 'success') {
            console.log(`[FormLayoutContext] Layout ${isDraft ? 'draft' : 'published'} saved successfully`);
            setSaveError(null);

            //  Save updated layout in session storage
            safeSetItem(localLayoutKey, adjustedLayout);
            const layoutHash = generateHash(adjustedLayout);
            safeSetItem(`${localLayoutKey}_hash`, layoutHash);

            setLayoutModified(false);
        } else {
            throw new Error(`Failed to ${isDraft ? 'save draft' : 'publish'}`);
        }
    } catch (error) {
        console.error(`[FormLayoutContext] Error ${isDraft ? 'saving draft' : 'publishing'}:`, error);
        setSaveError(error.message);
    } finally {
        setIsSaving(false);
    }
};


// Publishing the layout
const publishLayout = async () => {
    if (!formId || !layoutConfig) {
        console.error("[FormLayoutContext] Cannot publish layout: Missing formId or layoutConfig");
        return;
    }

    console.log("[FormLayoutContext: publishLayout] Preparing to publish layout...");

    setIsSaving(true);

    try {
        // Correct `submitRow` structure before publishing
        const adjustedLayout = adjustSubmitRow(layoutConfig);

        //  Ensure metadata alignment before publishing
        console.log("[FormLayoutContext: publishLayout] Ensuring metadata alignment before publishing...");
        metadataEnsured.current = false;
        ensureMetadataForLayout(adjustedLayout);
        metadataEnsured.current = true;

        //  Remove temporary metadata (if any exists)
        const storedMetadata =
          safeGetItem(localFieldsMetadataKey) ||
          safeGetItem(legacyFieldsMetadataKey) ||
          {};
        Object.keys(storedMetadata).forEach(fieldId => {
            if (storedMetadata[fieldId]?.isLoading) {
                console.warn(`[FormLayoutContext: publishLayout] Removing incomplete metadata for field ID: ${fieldId}`);
                delete storedMetadata[fieldId];
                deleteFieldMetadata(fieldId);
            }
        });
        safeSetItem(localFieldsMetadataKey, fieldsMetadata);
        safeSetItem(legacyFieldsMetadataKey, fieldsMetadata); // optional migration


        //  Save adjusted layout
        const additionalMetadata = { isDraft: false };
        const response = await saveLayoutConfig(formId, adjustedLayout, additionalMetadata);

        if (response.status === 'success') {
            console.log("[FormLayoutContext] Layout published successfully.");
            setSaveError(null);

            //  Save updated layout in session storage
            safeSetItem(localLayoutKey, adjustedLayout);
            const layoutHash = generateHash(adjustedLayout);
            safeSetItem(`${localLayoutKey}_hash`, layoutHash);

            setLayoutModified(false);
        } else {
            throw new Error('Failed to publish layout');
        }
    } catch (error) {
        console.error('[FormLayoutContext: publishLayout] Error publishing layout:', error);
        setSaveError(error.message);
    } finally {
        setIsSaving(false);
    }
};


// Memoized selectField using useCallback to prevent re-creation on every render
const selectField = useCallback((fieldId) => {
    if (!fieldId) {
//        console.warn("[FormLayoutContext] No valid fieldId provided to selectField.");
        return; // Early return if no valid fieldId is passed
    }

    // Find the field in layoutConfig
    const field = Object.values(layoutConfig).flat().find(f => f.id === fieldId);
    
    if (field) {
        // Fetch the updated metadata for this field from fieldsMetadata (now managed via FieldMetadataContext)
        const fieldMetadata = fieldsMetadata[fieldId];
        
        if (fieldMetadata) {
            const selectedFieldWithMetadata = {
                ...field,
                ...fieldMetadata,
            };
//            console.log("[FormLayoutContext] Field selected with full metadata:", selectedFieldWithMetadata);
            setSelectedField(selectedFieldWithMetadata);
        } else {
            console.warn(`[FormLayoutContext] No metadata found for field ID ${fieldId} in fieldsMetadata.`);
            setSelectedField(field); // Still set the field, but without metadata
        }
    } else {
        console.error("[FormLayoutContext] Field ID not found in layoutConfig:", fieldId);
    }
}, [layoutConfig, fieldsMetadata]);

/**
 * Helper function to rename row keys so there are no gaps (e.g. row0, row2 -> row0, row1).
 * Also ensures submitRow stays at the end, if present.
 */
function reindexRows(updatedLayout) {
    // Temporarily extract submitRow (if any) so we don't interfere with its key.
    let submitRowData = null;
    if (updatedLayout.submitRow) {
        submitRowData = updatedLayout.submitRow;
        delete updatedLayout.submitRow;
    }

    // Sort the remaining row keys (row0, row1, row2, etc.) in ascending order
    const sortedRowKeys = Object.keys(updatedLayout)
        .filter(key => key.startsWith('row'))
        .sort((a, b) => {
            const indexA = parseInt(a.replace('row', ''), 10);
            const indexB = parseInt(b.replace('row', ''), 10);
            return indexA - indexB;
        });

    // Rebuild a new layout object with continuous row keys (row0, row1, row2, ...)
    const newLayout = {};
    let rowCounter = 0;
    for (const oldKey of sortedRowKeys) {
        const newKey = `row${rowCounter}`;
        newLayout[newKey] = updatedLayout[oldKey];
        console.log(`[FormLayoutContext: reindexRows] Renaming ${oldKey} -> ${newKey}`);
        rowCounter++;
    }

    // If we extracted submitRow earlier, add it back to the end
    if (submitRowData) {
        newLayout.submitRow = submitRowData;
    }

    console.log("[FormLayoutContext: reindexRows] Final reindexed layout:", newLayout);
    return newLayout;
}


/**
 * Delete a field from the builder, keep layout & metadata in sync,
 * and guarantee the UI never drops to “spinner” state.
 */
const deleteField = async (fieldId) => {

  // ─────────────────────────────────────────────────────────────
  // bump edit epoch for user-driven layout mutation (delete).
  // Prevent stale Firestore hydration from overwriting this deletion.
  // ─────────────────────────────────────────────────────────────
  layoutEditEpoch.current += 1;
  console.log("[FormLayoutContext: deleteField] layoutEditEpoch bumped:", {
    epochNow: layoutEditEpoch.current,
    fieldId,
    formId,
    localLayoutKey,
    effectiveViewMode,
  });

  // ─────────────────────────────────────────────────────────────
  // cancel any in-flight Firestore hydrate so it cannot
  // overwrite this deletion if it finishes later.
  // ─────────────────────────────────────────────────────────────
  abortActiveFetch('deleteField');

  console.log(
    `[FormLayoutContext: deleteField] Starting deletion for field ID: ${fieldId} on form ID: ${formId}`
  );

  setIsLoading(true);


  try {
    /* ──────────────────────────────────────────────────────────────── */
    /* 1)  Cancel any in-flight temporary metadata                     */
    /* ──────────────────────────────────────────────────────────────── */
    if (activeMetadataPromises.current?.has(fieldId) || activeMetadataTimeouts.current?.has(fieldId)) {
      console.warn(
        `[FormLayoutContext: deleteField] Field ID ${fieldId} has pending metadata creation. Cancelling process.`
      );

      // Cancel any pending cleanup timeout (if it exists)
      const t = activeMetadataTimeouts.current.get(fieldId);
      if (t) clearTimeout(t);

      activeMetadataPromises.current.delete(fieldId);
      activeMetadataTimeouts.current.delete(fieldId);

      deleteFieldMetadata(fieldId);
      console.warn(
        `[FormLayoutContext: deleteField] Temporary metadata for field ID ${fieldId} removed.`
      );
    }


    /* ──────────────────────────────────────────────────────────────── */
    /* 2)  OPTIONAL – server-side removal in Firestore                 */
    /* ──────────────────────────────────────────────────────────────── */
    // console.log(`[FormLayoutContext: deleteField] Removing field ${fieldId} from Firestore…`);
    // await deleteFieldFromForm(fieldId, formId);   // ← uncomment when you’re ready

    /* ──────────────────────────────────────────────────────────────── */
    /* 3)  Update the local layout object                              */
    /* ──────────────────────────────────────────────────────────────── */
    if (!layoutConfig) {
      console.warn(
        '[FormLayoutContext: deleteField] layoutConfig is null or undefined, skipping local layout update.'
      );
      return;
    }

    const updatedLayout = { ...layoutConfig };

    Object.keys(updatedLayout).forEach((rowKey) => {
      updatedLayout[rowKey] = updatedLayout[rowKey].filter(
        (f) => f.id !== fieldId
      );

      if (updatedLayout[rowKey].length === 0) {
        delete updatedLayout[rowKey];
        console.log(
          `[FormLayoutContext: deleteField] Removed empty row: ${rowKey}`
        );
      }
    });

    let reindexedLayout = reindexRows(updatedLayout);

    // 🛡️ guarantee at least one editable row
    if (!Object.keys(reindexedLayout).some((k) => k.startsWith('row'))) {
      reindexedLayout.row0 = [];
      console.log(
        '[FormLayoutContext: deleteField] Added empty row0 to preserve drop zones.'
      );
    }

    // persist to storage
    safeSetItem(localLayoutKey, reindexedLayout);                         // ← keep key consistent
    safeSetItem(`${localLayoutKey}_hash`, generateHash(reindexedLayout)); // ← NEW: keep hash in sync

    // commit to React state BEFORE metadata sync
    setLayoutConfig(reindexedLayout);

    /* ──────────────────────────────────────────────────────────────── */
    /* 4)  Enhanced metadata cleanup                                   */
    /* ──────────────────────────────────────────────────────────────── */
    console.log(
      `[FormLayoutContext: deleteField] Deleting metadata for field ID: ${fieldId} in FieldMetadataContext.`
    );


    const storedMetadata =
      safeGetItem(localFieldsMetadataKey) ||
      safeGetItem(legacyFieldsMetadataKey) ||
      {};

    if (storedMetadata[fieldId]?.isLoading) {
      console.warn(
        `[FormLayoutContext: deleteField] Warning: Field ID ${fieldId} has incomplete metadata. Removing.`
      );
    }

    deleteFieldMetadata(fieldId);

    if (storedMetadata[fieldId]) {
      console.warn(
        `[FormLayoutContext: deleteField] Warning: Metadata for field ID ${fieldId} found in storage after deletion. Removing.`
      );
      delete storedMetadata[fieldId];
        safeSetItem(localFieldsMetadataKey, storedMetadata);
        safeSetItem(legacyFieldsMetadataKey, storedMetadata); // optional migration
    }

    console.log(
      `[FormLayoutContext: deleteField] Metadata for field ID ${fieldId} successfully removed.`
    );

    /* ──────────────────────────────────────────────────────────────── */
    /* 5)  Purge any field-specific cache / theme / dependency data    */
    /* ──────────────────────────────────────────────────────────────── */
    console.log(
      `[FormLayoutContext: deleteField] Removing all session/local storage data for field ID: ${fieldId} using safeRemoveFieldData.`
    );
    safeRemoveFieldData(formId, fieldId);

    /* ──────────────────────────────────────────────────────────────── */
    /* 6)  Re-sync metadata with the **new** layout                    */
    /* ──────────────────────────────────────────────────────────────── */
    console.log(
      `[FormLayoutContext: deleteField] Ensuring metadata alignment after deletion.`
    );
    metadataEnsured.current = false;
    ensureMetadataForLayout(reindexedLayout);
    metadataEnsured.current = true;

    /* ──────────────────────────────────────────────────────────────── */
    /* 7)  Clear selection if the deleted field was selected           */
    /* ──────────────────────────────────────────────────────────────── */
    if (selectedField?.id === fieldId) {
      setSelectedField(null);
      console.log(
        '[FormLayoutContext: deleteField] Cleared selectedField after deletion.'
      );
    }

    /* ──────────────────────────────────────────────────────────────── */
    /* 8)  Mark layout dirty                                           */
    /* ──────────────────────────────────────────────────────────────── */
    console.log(
      `[FormLayoutContext: deleteField] Field ${fieldId} deletion completed successfully.`
    );
    setLayoutModified(true);
  } catch (error) {
    console.error(
      `[FormLayoutContext: deleteField] Error deleting field ${fieldId}:`,
      error
    );
    setSaveError(`Error deleting field: ${error.message}`);
  } finally {
    setIsLoading(false);
  }
};


// New effect to trigger metadata ensuring once metadata has finished loading
useEffect(() => {
  // Metadata ensuring is an authoring concern.
  // Viewer/embed routes render the published metadata snapshot and must not
  // repair or mutate metadata at runtime.
  if (isViewerRoute) return;

  if (!fieldsLoading && formId && Object.keys(layoutConfig).length > 0) {
//    console.log("[FormLayoutContext] Metadata has finished loading; now ensuring metadata for layout.");
    ensureMetadataForLayout(layoutConfig);
  }
}, [
  fieldsLoading,
  formId,
  layoutConfig,
  ensureMetadataForLayout,
  isViewerRoute,
]);



// Memoize the contextValue to avoid re-calculating on each render
const contextValue = useMemo(() => ({
    layoutConfig,
    setLayoutConfig,
    fieldsMetadata,
    updateFieldMetadata,
    addFieldMetadata,
    deleteFieldMetadata,
    deleteField,
    showDropZones,
    setShowDropZones,
    selectField,
    selectedField,
    setSelectedField,
    isSaving,
    setIsSaving,
    saveError,
    setSaveError,
    ensureMetadataForLayout,
    fetchLatestLayout,
    handleDrop,
    handleDragStart,
    handleDragEnd,
    publishLayout,
    saveLayout
}), [
    layoutConfig,
    fieldsMetadata,
    updateFieldMetadata,
    addFieldMetadata,
    deleteFieldMetadata,
    showDropZones,
    selectedField,
    isSaving,
    saveError,
    ensureMetadataForLayout,
    fetchLatestLayout,
    selectField
]);

// Return the context provider with the memoized contextValue
return (
    <FormLayoutContext.Provider value={{
        layoutConfig,
        setLayoutConfig,
        fieldsMetadata,
        updateFieldMetadata,
        addFieldMetadata,
        deleteFieldMetadata,
        deleteField,
        showDropZones,
        activeDragId,
        setShowDropZones,
        handleDrop,
        handleDragStart,
        handleDragEnd,
        handleFieldChange,
        publishLayout,
        isSaving,
        saveLayout,
        isLoading,
        setIsLoading,
        selectedField,
        selectField,
        fetchLatestLayout,
        layoutVersionMode,
        setLayoutVersionMode,
    }}>
        {children}
    </FormLayoutContext.Provider>
);
};

export default FormLayoutProvider;
